import { BOARD_PATH, PLAYER_COLORS, COSTARRICANISMOS, ADIVINANZAS, LEGENDS_DATA, PROVINCES, TileType } from './data.js';
import { GoogleGenAI } from 'https://esm.sh/@google/genai@1.34.0';

const appEl = document.getElementById('app');
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const BOARD_ROWS = 7;
const BOARD_COLS = 5;
const TOTAL_TILES = BOARD_ROWS * BOARD_COLS;
const MINIGAME_COUNT = 3;
const SURPRISE_COUNT = 4;
const FAIR_COUNT = 1;
const PACT_COUNT = 1;
const MONA_LEGEND_ID = 'la_mona_costa_rica';
const LEGEND_TILE_TYPES = {
  mico_malo: TileType.NORMAL,
  carreta_sin_bueyes: TileType.PENALTY,
  la_segua: TileType.THEFT,
  la_llorona: TileType.THEFT,
  tulevieja: TileType.PENALTY,
  dueno_monte: TileType.NORMAL,
  los_duendes: TileType.NORMAL,
  sanatorio_duran: TileType.PENALTY,
  brujas_escazu: TileType.NORMAL,
  iztaru: TileType.NORMAL,
  hormigas_nandayure: TileType.THEFT,
  padre_sin_cabeza: TileType.BONUS,
  ninguenta: TileType.BONUS,
  la_mona_costa_rica: TileType.NORMAL,
  isla_san_lucas: TileType.NORMAL,
  rio_oro: TileType.NORMAL,
  copito: TileType.NORMAL,
  oso_caballo: TileType.NORMAL
};

const MALDICION_LEGENDS = new Set([
  'mico_malo',
  'carreta_sin_bueyes',
  'la_segua',
  'la_llorona',
  'tulevieja',
  'dueno_monte',
  'los_duendes',
  'sanatorio_duran',
  'hormigas_nandayure',
  'iztaru',
  'rio_oro',
  'oso_caballo'
]);

const visualIndices = (() => {
  const grid = [];
  for (let r = 0; r < BOARD_ROWS; r++) {
    const row = [];
    for (let c = 0; c < BOARD_COLS; c++) row.push(r * BOARD_COLS + c);
    if (r % 2 !== 0) row.reverse();
    grid.push(row);
  }
  return grid.flat();
})();

let gameState = {
  players: [],
  currentPlayerIndex: 0,
  isRolling: false,
  diceResult: null,
  gameLog: ['¡Bienvenidos a la Ruta de los Espantos!'],
  winner: null,
  finishCount: 0,
  narratorComment: 'Selecciona un modo para empezar.',
  status: 'menu',
  isHost: false,
  activeEvent: null,
  activeChoice: null,
  boardPath: null,
  galloPintoUsed: false,
  monaEnabled: false,
  monaVote: null,
  sanLucasVote: null,
  rioOroRiddle: null,
  endBlockedTurns: 0
};

let peer = null;
let connections = [];
let myId = '';
let stateRequestTimer = null;

let uiState = {
  inputRoomCode: '',
  playerName: '',
  viewingLegend: null,
  uiAlert: null,
  qrDataUrl: '',
  isScannerOpen: false,
  showTargetSelector: null,
  casadoTargetId: null,
  stealTargetId: null,
  caldosaStealTargetId: null,
  caldosaShareTargetId: null,
  isHelpOpen: false,
  isInventoryOpen: false,
  isTurnoVisible: false,
  turnoRoll: null,
  isTurnoRolling: false,
  isDuenoVisible: false,
  duenoRoll: null,
  isDuenoRolling: false,
  isBrujasVisible: false,
  brujasRoll: null,
  isBrujasRolling: false,
  showLegendSelector: null,
  numLocalPlayers: 2,
  monaEnabled: false
};

let qrScanner = null;
let qrRetryTimer = null;
let qrRetryCount = 0;

const apiKey = window.APP_CONFIG?.geminiApiKey || '';
const ai = apiKey ? new GoogleGenAI({ apiKey }) : null;

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function shuffleArray(items) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function pickNonAdjacentIndices(available, count, blocked = []) {
  const blockedSet = new Set(blocked);
  const isAdjacent = (idx, selected) => selected.some((s) => Math.abs(s - idx) === 1);
  const isAdjacentToBlocked = (idx) => blocked.some((b) => Math.abs(b - idx) === 1);
  for (let attempt = 0; attempt < 100; attempt++) {
    const shuffled = shuffleArray(available);
    const selected = [];
    for (const idx of shuffled) {
      if (selected.length >= count) break;
      if (blockedSet.has(idx)) continue;
      if (isAdjacent(idx, selected)) continue;
      if (isAdjacentToBlocked(idx)) continue;
      selected.push(idx);
    }
    if (selected.length === count) return selected;
  }
  return available.filter((idx) => !blockedSet.has(idx)).slice(0, count);
}

function getRandomProvince() {
  return PROVINCES[Math.floor(Math.random() * PROVINCES.length)];
}

function getLegendPool(isMonaEnabled) {
  const ids = Object.keys(LEGENDS_DATA);
  return isMonaEnabled ? ids : ids.filter((id) => id !== MONA_LEGEND_ID);
}

function createBoard(legendIds) {
  const board = Array.from({ length: TOTAL_TILES }, (_, idx) => ({
    id: idx,
    type: TileType.NORMAL,
    label: `Casilla ${idx + 1}`,
    description: ''
  }));

  board[0] = { id: 0, type: TileType.START, label: 'Inicio', description: '¡Empieza la aventura!', icon: '/susto.webp' };
  board[TOTAL_TILES - 1] = {
    id: TOTAL_TILES - 1,
    type: TileType.END,
    label: 'Meta',
    description: '¡Meta final!',
    icon: '/meta.webp'
  };

  const available = [];
  for (let i = 1; i < TOTAL_TILES - 1; i++) available.push(i);
  const minigameIndices = pickNonAdjacentIndices(available, MINIGAME_COUNT);
  const remainingAfterMinigame = available.filter((idx) => !minigameIndices.includes(idx));
  const surpriseIndices = pickNonAdjacentIndices(remainingAfterMinigame, SURPRISE_COUNT, minigameIndices);
  const remainingAfterSurprise = remainingAfterMinigame.filter((idx) => !surpriseIndices.includes(idx));
  const fairIndices = pickNonAdjacentIndices(remainingAfterSurprise, FAIR_COUNT, [...minigameIndices, ...surpriseIndices]);
  const remainingAfterFair = remainingAfterSurprise.filter((idx) => !fairIndices.includes(idx));
  const pactIndices = pickNonAdjacentIndices(
    remainingAfterFair,
    PACT_COUNT,
    [...minigameIndices, ...surpriseIndices, ...fairIndices]
  );
  const remainingAfterPact = remainingAfterFair.filter((idx) => !pactIndices.includes(idx));
  const shuffled = shuffleArray(remainingAfterPact);

  minigameIndices.forEach((idx) => {
    board[idx] = { ...board[idx], type: TileType.MINIGAME, label: 'Turno', description: '¡Minijuego!', icon: '/turno.webp' };
  });
  surpriseIndices.forEach((idx) => {
    board[idx] = { ...board[idx], type: TileType.SURPRISE, label: 'Caja Sorpresa', description: '¡Suerte!', icon: '/sorpresa.webp' };
  });
  fairIndices.forEach((idx) => {
    board[idx] = { ...board[idx], type: TileType.FAIR, label: 'Feria del Agricultor', description: '¡Comida y suerte!', icon: '/feria.webp' };
  });

  pactIndices.forEach((idx) => {
    board[idx] = { ...board[idx], type: TileType.PACT, label: 'Pacto del Jocote', description: 'Activa una alianza entre dos jugadores.', icon: '/jocote.webp' };
  });
  const legendSlots = shuffled;
  const legendPool = (legendIds && legendIds.length) ? legendIds : Object.keys(LEGENDS_DATA);
  const monaIncluded = legendPool.includes(MONA_LEGEND_ID);
  const shuffledLegends = shuffleArray(legendPool.filter((id) => id !== MONA_LEGEND_ID));
  if (monaIncluded) shuffledLegends.unshift(MONA_LEGEND_ID);
  shuffledLegends.forEach((legendId, i) => {
    if (i >= legendSlots.length) return;
    const idx = legendSlots[i];
    board[idx] = {
      ...board[idx],
      type: LEGEND_TILE_TYPES[legendId] || TileType.NORMAL,
      label: LEGENDS_DATA[legendId]?.name || board[idx].label,
      legendId
    };
  });

  const provincePool = shuffleArray(PROVINCES);
  let provinceIdx = 0;
  board.forEach((tile) => {
    if (tile.type !== TileType.NORMAL || tile.legendId) return;
    const province = provincePool[provinceIdx % provincePool.length];
    tile.label = province;
    tile.description = 'Provincia de Costa Rica.';
    if (province === 'Guanacaste') tile.icon = '/guana.webp';
    if (province === 'Heredia') tile.icon = '/heredia.webp';
    if (province === 'Cartago') tile.icon = '/cartago.webp';
    if (province === 'Limón') tile.icon = '/limon.webp';
    if (province === 'San José') tile.icon = '/san_jose.webp';
    if (province === 'Alajuela') tile.icon = '/alajuela.webp';
    if (province === 'Puntarenas') tile.icon = '/puntarenas.webp';
    provinceIdx += 1;
  });

  return board;
}

function getBoard() {
  return gameState.boardPath || BOARD_PATH;
}

function buildLegendTile(tile, legendId) {
  return {
    ...tile,
    type: LEGEND_TILE_TYPES[legendId] || TileType.NORMAL,
    label: LEGENDS_DATA[legendId]?.name || tile.label,
    legendId
  };
}

function maybeSwapMonaLegend() {
  if (!gameState.monaEnabled) return;
  const board = getBoard();
  const monaIndex = board.findIndex((tile) => tile.legendId === MONA_LEGEND_ID);
  if (monaIndex === -1) return;

  const otherLegendIndices = board
    .map((tile, idx) => (tile.legendId && tile.legendId !== MONA_LEGEND_ID ? idx : -1))
    .filter((idx) => idx >= 0);

  if (!otherLegendIndices.length) return;
  if (Math.random() >= 0.5) return;

  const targetIdx = otherLegendIndices[Math.floor(Math.random() * otherLegendIndices.length)];
  const targetLegendId = board[targetIdx].legendId;
  if (!targetLegendId) return;

  const updatedBoard = board.map((tile, idx) => {
    if (idx === monaIndex) return buildLegendTile(tile, targetLegendId);
    if (idx === targetIdx) return buildLegendTile(tile, MONA_LEGEND_ID);
    return tile;
  });

  const s = {
    ...gameState,
    boardPath: updatedBoard,
    gameLog: [`La Mona cambio de lugar con ${LEGENDS_DATA[targetLegendId]?.name || targetLegendId}.`, ...gameState.gameLog].slice(0, 8)
  };
  setGameState(s);
  broadcastState(s);
}

function buildMonaVoteChoice(monaVote) {
  const targetIdx = gameState.players.findIndex((p) => p.id === monaVote.targetId);
  return {
    type: 'MONA_VOTE',
    title: 'Votacion de La Mona',
    message: `El jugador eligio ${monaVote.decision === 'VERDAD' ? 'Verdad' : 'Reto'}. Vota si cumplio.`,
    options: [
      { label: 'Aprobar', value: 'APPROVE', icon: '✅' },
      { label: 'Rechazar', value: 'REJECT', icon: '❌' }
    ],
    voters: monaVote.voters,
    votes: monaVote.votes,
    playerIdx: targetIdx >= 0 ? targetIdx : undefined,
    targetId: monaVote.targetId
  };
}

function registerMonaVote(voterId, value) {
  const monaVote = gameState.monaVote;
  if (!monaVote) return;
  if (!monaVote.voters.includes(voterId)) return;
  if (monaVote.votes[voterId]) return;

  const updatedVotes = { ...monaVote.votes, [voterId]: value };
  const updatedMonaVote = { ...monaVote, votes: updatedVotes };

  const allVoted = Object.keys(updatedVotes).length >= monaVote.voters.length;
  if (!allVoted) {
    const s = { ...gameState, monaVote: updatedMonaVote, activeChoice: buildMonaVoteChoice(updatedMonaVote) };
    setGameState(s);
    broadcastState(s);
    return;
  }

  const approveCount = Object.values(updatedVotes).filter((v) => v === 'APPROVE').length;
  const rejectCount = monaVote.voters.length - approveCount;
  let penalty = 2;
  let verdict = 'Empate';
  if (approveCount > rejectCount) {
    penalty = 1;
    verdict = 'Aprobado';
  } else if (rejectCount > approveCount) {
    penalty = 4;
    verdict = 'Rechazado';
  }

  window._lastChoice = { type: 'MONA_RESULT', penalty };
  const resultEvent = {
    title: 'La Mona decide',
    message: `${verdict}. Pierdes ${penalty} tamales.`,
    icon: LEGENDS_DATA[MONA_LEGEND_ID]?.icon || '🐒',
    type: penalty > 1 ? 'BAD' : 'NEUTRAL'
  };

  const finalState = {
    ...gameState,
    monaVote: null,
    activeChoice: null,
    activeEvent: resultEvent
  };
  setGameState(finalState);
  broadcastState(finalState);
}

function buildSanLucasVoteChoice(sanLucasVote) {
  return {
    type: 'SANLUCAS_VOTE',
    title: 'Isla San Lucas',
    message: 'Todos votan a quien juzgar.',
    voters: sanLucasVote.voters,
    votes: sanLucasVote.votes,
    candidates: sanLucasVote.candidates
  };
}

function registerSanLucasVote(voterId, targetId) {
  const sanLucasVote = gameState.sanLucasVote;
  if (!sanLucasVote) return;
  if (!sanLucasVote.voters.includes(voterId)) return;
  if (sanLucasVote.votes[voterId]) return;
  if (!sanLucasVote.candidates.includes(targetId)) return;

  const updatedVotes = { ...sanLucasVote.votes, [voterId]: targetId };
  const updatedSanLucasVote = { ...sanLucasVote, votes: updatedVotes };

  const allVoted = Object.keys(updatedVotes).length >= sanLucasVote.voters.length;
  if (!allVoted) {
    const s = { ...gameState, sanLucasVote: updatedSanLucasVote, activeChoice: buildSanLucasVoteChoice(updatedSanLucasVote) };
    setGameState(s);
    broadcastState(s);
    return;
  }

  const counts = sanLucasVote.candidates.reduce((acc, id) => {
    acc[id] = 0;
    return acc;
  }, {});
  Object.values(updatedVotes).forEach((id) => {
    counts[id] = (counts[id] || 0) + 1;
  });
  const maxVotes = Math.max(...Object.values(counts));
  const topTargets = Object.entries(counts)
    .filter(([, count]) => count === maxVotes)
    .map(([id]) => Number(id));

  let updatedPlayers = [...gameState.players];
  let resultMessage = '';
  if (topTargets.length > 1) {
    updatedPlayers = updatedPlayers.map((p) => ({ ...p, tamales: Math.max(0, p.tamales - 3) }));
    resultMessage = 'Empate. Todos pierden 3 tamales.';
  } else {
    const targetIdNum = topTargets[0];
    updatedPlayers = updatedPlayers.map((p) =>
      p.id === targetIdNum ? { ...p, tamales: Math.max(0, p.tamales - 2) } : p
    );
    const target = gameState.players.find((p) => p.id === targetIdNum);
    resultMessage = `${target?.name || 'Un jugador'} pierde 2 tamales.`;
  }

  const finalState = {
    ...gameState,
    players: updatedPlayers,
    sanLucasVote: null,
    activeChoice: null,
    activeEvent: {
      title: 'Isla San Lucas',
      message: resultMessage,
      icon: '/sanlucas.webp',
      type: 'BAD'
    }
  };
  setGameState(finalState);
  broadcastState(finalState);
}

function buildRioOroChoice(riddleState) {
  return {
    type: 'RIO_ORO',
    title: 'Rio Oro',
    message: riddleState.riddle,
    options: riddleState.options,
    voters: riddleState.voters,
    answers: riddleState.answers,
    playerIdx: riddleState.playerIdx
  };
}

function registerRioOroAnswer(voterId, answer) {
  const riddleState = gameState.rioOroRiddle;
  if (!riddleState) return;
  if (!riddleState.voters.includes(voterId)) return;
  if (riddleState.answers[voterId]) return;
  if (!riddleState.options.includes(answer)) return;

  const updatedAnswers = { ...riddleState.answers, [voterId]: answer };
  const updatedRiddle = { ...riddleState, answers: updatedAnswers };
  const allAnswered = Object.keys(updatedAnswers).length >= riddleState.voters.length;

  if (!allAnswered) {
    const s = { ...gameState, rioOroRiddle: updatedRiddle, activeChoice: buildRioOroChoice(updatedRiddle) };
    setGameState(s);
    broadcastState(s);
    return;
  }

  const correctCount = Object.values(updatedAnswers).filter((value) => value === riddleState.correctAnswer).length;
  const totalVotes = riddleState.voters.length;
  const success = correctCount > totalVotes / 2;

  window._lastChoice = { type: 'RIO_ORO', success };
  const resultEvent = {
    title: 'Rio Oro',
    message: success ? 'Adivinanza resuelta. Sigues en tu casilla.' : 'Fallaron la adivinanza. Todos retroceden 2 casillas.',
    icon: '/riooro.webp',
    type: success ? 'GOOD' : 'BAD'
  };

  const finalState = {
    ...gameState,
    rioOroRiddle: null,
    activeChoice: null,
    activeEvent: resultEvent
  };
  setGameState(finalState);
  broadcastState(finalState);
}

async function handleMonaCurse(playerIdx) {
  const target = gameState.players[playerIdx];
  if (!target) return 0;

  const decisionChoice = {
    playerIdx,
    type: 'MONA_DECISION',
    title: 'La Mona',
    message: 'Elige verdad o reto.',
    options: [
      { label: 'Verdad', value: 'VERDAD', icon: '🗣️' },
      { label: 'Reto', value: 'RETO', icon: '🔥' }
    ]
  };

  setGameState((prev) => {
    const s = { ...prev, activeChoice: decisionChoice };
    broadcastState(s);
    return s;
  });
  await waitForInteraction();
  const decision = window._lastChoice || 'RETO';
  window._lastChoice = null;
  const decisionText = decision === 'VERDAD' ? 'Verdad' : 'Reto';

  const instructionEvent = {
    title: 'Reto de La Mona',
    message: `${target.name} eligio ${decisionText}. Ponganse de acuerdo en la pregunta o desafio y luego voten si lo cumplio.`,
    icon: LEGENDS_DATA[MONA_LEGEND_ID]?.icon || '🐒',
    type: 'NEUTRAL'
  };
  setGameState((prev) => {
    const s = { ...prev, activeEvent: instructionEvent };
    broadcastState(s);
    return s;
  });
  await waitForInteraction();

  const voters = gameState.players
    .filter((p) => p.id !== target.id && !p.isFinished && p.tamales > 0)
    .map((p) => p.id);

  if (!voters.length) {
    window._lastChoice = { type: 'MONA_RESULT', penalty: 1 };
    const s = {
      ...gameState,
      activeEvent: {
        title: 'La Mona decide',
        message: 'Sin votos. Pierdes 1 tamal.',
        icon: LEGENDS_DATA[MONA_LEGEND_ID]?.icon || '🐒',
        type: 'NEUTRAL'
      }
    };
    setGameState(s);
    broadcastState(s);
    await waitForInteraction();
    return 1;
  }

  const monaVote = {
    targetId: target.id,
    voters,
    votes: {},
    decision
  };
  setGameState((prev) => {
    const s = { ...prev, monaVote, activeChoice: buildMonaVoteChoice(monaVote) };
    broadcastState(s);
    return s;
  });
  await waitForInteraction();

  const result = window._lastChoice?.penalty ?? 1;
  window._lastChoice = null;
  return result;
}

function setGameState(updater) {
  gameState = typeof updater === 'function' ? updater(gameState) : updater;
  render();
  return gameState;
}

function setUiState(patch) {
  uiState = { ...uiState, ...patch };
  render();
}

function showUiAlert(title, message, type = 'NEUTRAL') {
  setUiState({ uiAlert: { title, message, type } });
}

function scheduleQrRetry() {
  if (qrRetryTimer || qrRetryCount >= 10) return;
  qrRetryTimer = setTimeout(() => {
    qrRetryTimer = null;
    qrRetryCount += 1;
    render();
  }, 500);
}

function setMonaEnabled(value) {
  if (gameState.status === 'lobby' && gameState.isHost) {
    setGameState((prev) => {
      const s = { ...prev, monaEnabled: value };
      broadcastState(s);
      return s;
    });
    return;
  }
  setUiState({ monaEnabled: value });
}

function registerConnection(conn) {
  if (!conn || connections.includes(conn)) return;
  connections = [...connections, conn];
  conn.on('close', () => {
    connections = connections.filter((c) => c !== conn);
  });
  conn.on('error', (err) => {
    console.warn('Peer connection error:', err);
  });
}

function sendStateToConnection(conn, state) {
  if (!conn || !conn.open) return;
  try {
    conn.send({ type: 'SYNC_STATE', state });
  } catch (err) {
    console.warn('Broadcast failed:', err);
  }
}

function broadcastState(state) {
  connections.forEach((conn) => {
    sendStateToConnection(conn, state);
  });
}

function startStateHeartbeat() {
  if (stateRequestTimer || gameState.isHost) return;
  stateRequestTimer = setInterval(() => {
    const conn = connections[0];
    if (!conn || !conn.open) return;
    try {
      conn.send({ type: 'STATE_REQUEST' });
    } catch (err) {
      console.warn('State request failed:', err);
    }
  }, 3000);
}

function stopStateHeartbeat() {
  if (!stateRequestTimer) return;
  clearInterval(stateRequestTimer);
  stateRequestTimer = null;
}

function advanceTurnIfNeeded() {
  if (!gameState.isHost || gameState.status !== 'playing') return;
  if (gameState.isRolling || gameState.activeEvent || gameState.activeChoice || gameState.winner) return;
  if (!gameState.players.length) return;

  let idx = gameState.currentPlayerIndex;
  let iterations = 0;
  while ((gameState.players[idx].isFinished || gameState.players[idx].tamales <= 0) && iterations < gameState.players.length) {
    idx = (idx + 1) % gameState.players.length;
    iterations++;
  }
  if (iterations === 0 || iterations >= gameState.players.length) return;

  const s = { ...gameState, currentPlayerIndex: idx };
  setGameState(s);
  broadcastState(s);
}

function handleIncomingData(action, connection) {
  if (gameState.isHost) registerConnection(connection);
  if (action.type === 'SYNC_STATE') {
    setGameState(action.state);
  } else if (action.type === 'STATE_REQUEST') {
    if (gameState.isHost) sendStateToConnection(connection, gameState);
  } else if (action.type === 'PLAYER_JOINED') {
    if (gameState.isHost) {
      setGameState((prev) => {
        const peerId = action.player?.peerId || connection?.peer || '';
        const playerWithColor = {
          ...action.player,
          peerId,
          color: PLAYER_COLORS[prev.players.length % PLAYER_COLORS.length]
        };
        const newState = { ...prev, players: [...prev.players, playerWithColor] };
        broadcastState(newState);
        return newState;
      });
    }
  } else if (action.type === 'ROLL_DICE') {
    if (gameState.isHost) processDiceRoll();
  } else if (action.type === 'USE_ITEM') {
    if (gameState.isHost) processItemUsage(action.itemId, action.targetId, action.fromId, action.destinationPos, action.stolenItemId, action.removeLegendId, action.shareTargetId);
  } else if (action.type === 'CLOSE_EVENT') {
    if (gameState.isHost) {
      setGameState((prev) => {
        const newState = { ...prev, activeEvent: null };
        broadcastState(newState);
        return newState;
      });
    }
  } else if (action.type === 'MAKE_CHOICE') {
    if (gameState.isHost) {
      setGameState((prev) => {
        const newState = { ...prev, activeChoice: null };
        window._lastChoice = action.value;
        broadcastState(newState);
        return newState;
      });
    }
  } else if (action.type === 'MONA_VOTE') {
    if (gameState.isHost) registerMonaVote(action.voterId, action.value);
  } else if (action.type === 'SANLUCAS_VOTE') {
    if (gameState.isHost) registerSanLucasVote(action.voterId, action.targetId);
  } else if (action.type === 'RIO_ORO_ANSWER') {
    if (gameState.isHost) registerRioOroAnswer(action.voterId, action.answer);
  }
}

function resetToMenu() {
  if (!window.confirm('¿Seguro?')) return;
  connections.forEach((c) => c.close());
  connections = [];
  stopStateHeartbeat();
  setGameState({
    players: [],
    currentPlayerIndex: 0,
    isRolling: false,
    diceResult: null,
    gameLog: ['Ruta reiniciada.'],
    winner: null,
    finishCount: 0,
    narratorComment: 'Selecciona modo.',
    status: 'menu',
    roomCode: undefined,
    isHost: false,
    activeEvent: null,
    activeChoice: null,
    boardPath: null,
    galloPintoUsed: false,
    monaEnabled: false,
    monaVote: null,
    sanLucasVote: null,
    rioOroRiddle: null,
    endBlockedTurns: 0
  });
  setUiState({
    qrDataUrl: '',
    isScannerOpen: false,
    showTargetSelector: null,
    casadoTargetId: null,
    stealTargetId: null,
    caldosaStealTargetId: null,
    caldosaShareTargetId: null,
    isHelpOpen: false,
    isInventoryOpen: false,
    isTurnoVisible: false,
    turnoRoll: null,
    isTurnoRolling: false,
    isDuenoVisible: false,
    duenoRoll: null,
    isDuenoRolling: false,
    isBrujasVisible: false,
    brujasRoll: null,
    isBrujasRolling: false,
    showLegendSelector: null,
    monaEnabled: false
  });
}

function initializeMatch() {
  if (!gameState.isHost) return;
  const restartedPlayers = gameState.players.map((p) => ({
    ...p,
    position: 0,
    tamales: 10,
    isDrunk: false,
    isSkipped: false,
    skipSourceName: null,
    isFinished: false,
    inventory: [],
    arroz: 0,
    slowTurns: 0,
    pactPartnerId: null,
    pactTurns: 0
  }));
  const newState = {
    ...gameState,
    players: restartedPlayers,
    currentPlayerIndex: 0,
    diceResult: null,
    isRolling: false,
    winner: null,
    finishCount: 0,
    status: 'playing',
    gameLog: ['¡Empezamos!'],
    activeEvent: null,
    activeChoice: null,
    boardPath: createBoard(getLegendPool(gameState.monaEnabled)),
    galloPintoUsed: false,
    monaVote: null,
    sanLucasVote: null,
    rioOroRiddle: null,
    endBlockedTurns: 0
  };
  setGameState(newState);
  broadcastState(newState);
}

function startLocalGame() {
  const initialPlayers = PLAYER_COLORS.slice(0, uiState.numLocalPlayers).map((color, i) => ({
    id: i + 1,
    name: `Jugador ${i + 1}`,
    color,
    position: 0,
    tamales: 10,
    isDrunk: false,
    isSkipped: false,
    skipSourceName: null,
    isFinished: false,
    inventory: [],
    arroz: 0,
    slowTurns: 0,
    pactPartnerId: null,
    pactTurns: 0
  }));
  setGameState((prev) => ({
    ...prev,
    status: 'playing',
    players: initialPlayers,
    isHost: true,
    roomCode: undefined,
    boardPath: createBoard(getLegendPool(uiState.monaEnabled)),
    galloPintoUsed: false,
    monaEnabled: uiState.monaEnabled,
    monaVote: null,
    sanLucasVote: null,
    rioOroRiddle: null,
    endBlockedTurns: 0
  }));
}

function createRoom() {
  if (!uiState.playerName) {
    showUiAlert('Falta nombre', 'Escribe tu nombre para continuar.', 'BAD');
    return;
  }
  if (!myId) {
    showUiAlert('Servidor de salas', 'A?n conectando. Espera unos segundos y prueba de nuevo.', 'NEUTRAL');
    return;
  }
  if (!peer) {
    showUiAlert('Servidor de salas', 'Aún conectando. Espera unos segundos y prueba de nuevo.', 'NEUTRAL');
    return;
  }
  const hostPlayer = {
    id: 1,
    peerId: myId,
    name: uiState.playerName,
    color: PLAYER_COLORS[0],
    position: 0,
    tamales: 10,
    isDrunk: false,
    isSkipped: false,
    skipSourceName: null,
    isFinished: false,
    inventory: [],
    arroz: 0,
    slowTurns: 0,
    pactPartnerId: null,
    pactTurns: 0
  };
  setGameState((prev) => ({
    ...prev,
    status: 'lobby',
    isHost: true,
    players: [hostPlayer],
    roomCode: myId,
    monaEnabled: uiState.monaEnabled,
    monaVote: null,
    sanLucasVote: null,
    rioOroRiddle: null,
    endBlockedTurns: 0
  }));
  setUiState({ qrDataUrl: '' });
}

function joinRoom(code) {
  const targetCode = code || uiState.inputRoomCode;
  if (!uiState.playerName) {
    showUiAlert('Falta nombre', 'Escribe tu nombre para continuar.', 'BAD');
    return;
  }
  if (!targetCode) {
    showUiAlert('Falta código', 'Escribe el código de sala para unirte.', 'BAD');
    return;
  }
  if (!myId) {
    showUiAlert('Servidor de salas', 'A?n conectando. Espera unos segundos y prueba de nuevo.', 'NEUTRAL');
    return;
  }
  if (!peer) {
    showUiAlert('Servidor de salas', 'Aún conectando. Espera unos segundos y prueba de nuevo.', 'NEUTRAL');
    return;
  }
  const conn = peer.connect(targetCode.toLowerCase());
  conn.on('error', (err) => {
    console.error('Peer connection error:', err);
    showUiAlert('No se pudo conectar', 'Verifica el código y tu conexión.', 'BAD');
  });
  conn.on('close', () => {
    stopStateHeartbeat();
  });
  conn.on('open', () => {
    connections = [conn];
    const newPlayer = {
      id: Date.now(),
      peerId: myId,
      name: uiState.playerName,
      color: PLAYER_COLORS[1],
      position: 0,
      tamales: 10,
      isDrunk: false,
      isSkipped: false,
      skipSourceName: null,
      isFinished: false,
      inventory: [],
      arroz: 0,
      slowTurns: 0,
      pactPartnerId: null,
      pactTurns: 0
    };
    conn.send({ type: 'PLAYER_JOINED', player: newPlayer });
    setGameState((prev) => ({ ...prev, status: 'lobby', isHost: false, roomCode: targetCode }));
    startStateHeartbeat();
  });
  conn.on('data', (data) => handleIncomingData(data, conn));
}

function waitForInteraction() {
  return new Promise((resolve) => {
    const check = setInterval(() => {
      if (!gameState.activeEvent && !gameState.activeChoice) {
        clearInterval(check);
        resolve();
      }
    }, 500);
  });
}

function handleTurnoMinigame() {
  setUiState({ isTurnoVisible: true });
  return new Promise((resolve) => {
    window._solveTurno = (rollValue) => {
      let tamales = 0;
      let item = undefined;
      let event;
  switch (rollValue) {
    case 6:
      item = 'Casado';
      event = { title: '¡PREMIO!', message: '¡Un Casado!', icon: '🍱', type: 'GOOD' };
      break;
    case 5:
      item = 'Chifrijo';
      event = { title: '¡Rico!', message: '¡Un Chifrijo!', icon: '🍲', type: 'GOOD' };
      break;
    case 4:
      tamales = 15;
      event = { title: '¡Pura Vida!', message: '¡15 tamales!', icon: '👨‍🌾', type: 'GOOD' };
      break;
    case 3:
      item = 'Algodón de Azúcar';
      event = { title: '¡Dulce!', message: '¡Algodón de Azúcar!', icon: '🍭', type: 'GOOD' };
      break;
    case 2:
      item = 'Suspiros Costarricenses';
      event = { title: 'Dulce!', message: 'Suspiros Costarricenses!', icon: getItemIcon('Suspiros Costarricenses'), type: 'GOOD' };
      break;
    case 1:
      tamales = 1;
      event = { title: 'Tamal!', message: 'Uno caliente!', icon: 'dY®"', type: 'GOOD' };
      break;
        default:
          event = { title: '¡Nada!', message: '¡Mala suerte!', icon: '💨', type: 'NEUTRAL' };
          break;
      }
      setUiState({ isTurnoVisible: false, turnoRoll: null, isTurnoRolling: false });
      resolve({ tamales, item, log: 'Turno completado', event });
    };
  });
}

function handleDuenoPenalty() {
  setUiState({ isDuenoVisible: true });
  return new Promise((resolve) => {
    window._solveDueno = (roll) => {
      setUiState({ isDuenoVisible: false, duenoRoll: null, isDuenoRolling: false });
      resolve(roll);
    };
  });
}

function handleBrujasCurse() {
  setUiState({ isBrujasVisible: true });
  return new Promise((resolve) => {
    window._solveBrujas = (roll) => {
      setUiState({ isBrujasVisible: false, brujasRoll: null, isBrujasRolling: false });
      resolve(roll);
    };
  });
}

async function getSpookyComment(contextType, playerName, extraInfo) {
  if (!ai) return '¡El bosque guarda silencio!';
  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `You are "El Narrador de Leyendas", a mysterious and witty host of a Costa Rican folklore board game. 
      ${
        contextType === 'SABOTAGE'
          ? `Player "${playerName}" just got sabotaged with a "Trago de Cacique" by ${extraInfo}. They will skip their next turn.`
          : `Player "${playerName}" just landed on a tile of type "${contextType}".`
      }
      Write a very short (max 2 sentences) spooky or funny comment in Spanish about what happened, referencing Costa Rican folklore or local drinking culture/slang (yodo, jarana, fiesta, etc.). 
      Be thematic and slightly mischievous.`,
      config: {
        temperature: 0.8,
        maxOutputTokens: 100
      }
    });

    return response.text?.trim() || '¡El bosque guarda silencio!';
  } catch (error) {
    console.error('Gemini Error:', error);
    return '¡Las sombras se agitan en la noche!';
  }
}
async function processDiceRoll() {
  if (gameState.isRolling || gameState.winner || gameState.activeEvent || gameState.activeChoice) return;

  let currentPlayerIdx = gameState.currentPlayerIndex;
  let iterations = 0;
  while ((gameState.players[currentPlayerIdx].isFinished || gameState.players[currentPlayerIdx].tamales <= 0) && iterations < gameState.players.length) {
    currentPlayerIdx = (currentPlayerIdx + 1) % gameState.players.length;
    iterations++;
  }

  let currentPlayer = gameState.players[currentPlayerIdx];
  let endBlockedTurns = gameState.endBlockedTurns || 0;
  let endBlockJustSet = false;

  if (currentPlayer.isSkipped) {
    const skipSourceName = currentPlayer.skipSourceName;
    const newPlayers = [...gameState.players];
    newPlayers[currentPlayerIdx] = { ...currentPlayer, isSkipped: false, skipSourceName: null };
    if (endBlockedTurns > 0) endBlockedTurns -= 1;
    const skippedState = {
      ...gameState,
      players: newPlayers,
      currentPlayerIndex: (currentPlayerIdx + 1) % gameState.players.length,
      endBlockedTurns,
      activeEvent: {
        title: 'Trago de Cacique',
        message: `${skipSourceName || 'Un rival'} le tiro un Cacique a ${currentPlayer.name}. Pierde el turno.`,
        icon: getItemIcon('Trago de Cacique'),
        type: 'BAD'
      }
    };
    setGameState(skippedState);
    broadcastState(skippedState);
    return;
  }

  maybeSwapMonaLegend();

  let workingState = { ...gameState, isRolling: true, diceResult: null, currentPlayerIndex: currentPlayerIdx, endBlockedTurns };
  setGameState(workingState);
  broadcastState(workingState);

  await sleep(1000);
  const roll = Math.floor(Math.random() * 6) + 1;
  workingState = { ...workingState, diceResult: roll };
  setGameState(workingState);
  broadcastState(workingState);

  await sleep(600);

  let moveAmount = currentPlayer.isDrunk ? -Math.floor(roll / 2) : roll;
  if (!currentPlayer.isDrunk && (currentPlayer.slowTurns || 0) > 0) {
    moveAmount = Math.min(2, moveAmount);
  }
  const board = getBoard();
  const endIndex = board.length - 1;
  let finalPos = Math.min(endIndex, Math.max(0, currentPlayer.position + moveAmount));
  let blockedLanding = false;
  if (endBlockedTurns > 0 && finalPos === endIndex) {
    finalPos = Math.max(0, endIndex - 1);
    blockedLanding = true;
  }

  let currentPos = currentPlayer.position;
  while (currentPos !== finalPos) {
    currentPos += finalPos > currentPos ? 1 : -1;
    const stepPlayers = [...workingState.players];
    stepPlayers[currentPlayerIdx] = { ...stepPlayers[currentPlayerIdx], position: currentPos };
    workingState = { ...workingState, players: stepPlayers };
    setGameState(workingState);
    broadcastState(workingState);
    await sleep(300);
  }

  if (blockedLanding) {
    workingState = {
      ...workingState,
      activeEvent: {
        title: '¡Meta bloqueada!',
        message: 'Debes esperar en la casilla anterior hasta que pase el bloqueo.',
        icon: '⛓️',
        type: 'BAD'
      }
    };
    setGameState(workingState);
    broadcastState(workingState);
    await waitForInteraction();
    syncState();
  }

  const tile = board[finalPos];
  let newInventory = [...workingState.players[currentPlayerIdx].inventory];
  let finalTamales = workingState.players[currentPlayerIdx].tamales;
  let finalArroz = workingState.players[currentPlayerIdx].arroz || 0;
  let skipPenalty = false;
  const skipTileEffects = blockedLanding;

  const syncState = () => { workingState = gameState; };
  const applyPactLoss = (loss) => {
    if (loss <= 0) return;
    const current = workingState.players[currentPlayerIdx];
    const partnerId = current?.pactPartnerId;
    const turns = current?.pactTurns || 0;
    if (!partnerId || turns <= 0) return;
    const partnerIdx = workingState.players.findIndex((p) => p.id === partnerId);
    if (partnerIdx < 0) return;
    const shared = Math.floor(loss / 2);
    if (shared <= 0) return;
    const updatedPlayers = workingState.players.map((p, idx) =>
      idx === partnerIdx ? { ...p, tamales: Math.max(0, p.tamales - shared) } : p
    );
    workingState = { ...workingState, players: updatedPlayers };
  };
  const loseTamales = (loss, withPact = false) => {
    finalTamales = Math.max(0, finalTamales - loss);
    if (withPact) applyPactLoss(loss);
  };

  if (!skipTileEffects && tile.type === TileType.MINIGAME) {
    const res = await handleTurnoMinigame(currentPlayerIdx);
    finalTamales += res.tamales;
    if (res.item) newInventory.push(res.item);
    workingState = { ...workingState, activeEvent: res.event };
    setGameState(workingState);
    broadcastState(workingState);
    await waitForInteraction();
    syncState();
  } else if (!skipTileEffects && tile.type === TileType.SURPRISE) {
    const rand = Math.random();
    let item = '';
    let ev;
    if (!gameState.galloPintoUsed && rand < 0.12) {
      item = 'Gallo Pinto';
      ev = { title: '¡Gallo Pinto!', message: 'Puedes eliminar una leyenda del tablero.', icon: '🍛', type: 'GOOD' };
      workingState = { ...workingState, galloPintoUsed: true };
    } else if (rand < 0.22) {
      item = 'Casado';
      ev = { title: '¡Casado!', message: 'Para mover rivales.', icon: '🍱', type: 'GOOD' };
    } else if (rand < 0.32) {
      item = 'Sanatorio';
      ev = { title: '¡Fantasmas!', message: 'Carta del Sanatorio Duran.', icon: '/duran.webp', type: 'GOOD' };
    } else if (rand < 0.40) {
      item = 'Campesino';
      ev = { title: '¡Campesino!', message: 'Te protege de maldiciones de leyendas (excepto La Mona).', icon: '👨‍🌾', type: 'GOOD' };
    } else if (rand < 0.52) {
      finalTamales += 10;
      ev = { title: '¡Pura Vida!', message: '10 tamales gratis.', icon: '🫔', type: 'GOOD' };
    } else if (rand < 0.72) {
      item = 'Rice and Beans';
      ev = { title: '¡Rice and Beans!', message: '¡Pura delicia! Cámbialo por cualquier objeto o comida de un rival.', icon: '🍚', type: 'GOOD' };
    } else if (rand < 0.82) {
      item = 'Suspiros Costarricenses';
      ev = { title: 'Suspiros Costarricenses', message: 'Ganas Suspiros Costarricenses.', icon: getItemIcon('Suspiros Costarricenses'), type: 'GOOD' };
    } else if (rand < 0.92) {
      item = 'Sopa de Mondongo';
      ev = { title: 'Sopa de Mondongo', message: 'Ganas una Sopa de Mondongo.', icon: getItemIcon('Sopa de Mondongo'), type: 'GOOD' };
    } else {
      item = 'Trago de Cacique';
      ev = { title: 'A­Salud!', message: 'Hace que un rival pierda su prA3ximo turno.', icon: getItemIcon('Trago de Cacique'), type: 'GOOD' };
    }
    if (item) newInventory.push(item);
    workingState = { ...workingState, activeEvent: ev };
    setGameState(workingState);
    broadcastState(workingState);
    await waitForInteraction();
    syncState();
  }

  if (!skipTileEffects && tile.legendId && tile.type !== TileType.SURPRISE && tile.type !== TileType.MINIGAME) {
    let legendToResolve = tile.legendId;
    let skipLegendEffect = false;
    let arrozChoiceMade = false;
    if (legendToResolve !== MONA_LEGEND_ID && newInventory.includes('Campesino')) {
      newInventory = newInventory.filter((i) => i !== 'Campesino');
      skipLegendEffect = true;
      workingState = {
        ...workingState,
        activeEvent: {
          title: '¡Proteccion Campesina!',
          message: 'El Campesino te protegio de la maldicion y se ha gastado.',
          icon: '👨‍🌾',
          type: 'GOOD'
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    }
    if (!skipLegendEffect && legendToResolve === 'brujas_escazu') {
      await handleBrujasCurse();
      const candidates = getLegendPool(gameState.monaEnabled).filter((id) => id !== 'brujas_escazu');
      const selected = candidates[Math.floor(Math.random() * candidates.length)];
      workingState = {
        ...workingState,
        activeEvent: {
          title: '¡Maldición!',
          message: `Las brujas invocan a ${LEGENDS_DATA[selected].name}.`,
          icon: LEGENDS_DATA[selected].icon || '🧙‍♀️',
          type: 'BAD'
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
      legendToResolve = selected;
    }

    if (!skipLegendEffect && !arrozChoiceMade && MALDICION_LEGENDS.has(legendToResolve) && finalArroz >= 3) {
      arrozChoiceMade = true;
      workingState = {
        ...workingState,
        activeChoice: {
          playerIdx: currentPlayerIdx,
          type: 'ARROZ',
          title: '¡Arroz con Leche!',
          message: '¿Quieres usar 3 para evitar la maldición de esta leyenda?',
          options: [
            { label: 'Evitar maldición (-3)', value: 'AVOID', icon: '🍮' },
            { label: 'Aceptar maldición', value: 'TAKE', icon: '💀' }
          ]
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      if (window._lastChoice === 'AVOID') {
        finalArroz = Math.max(0, finalArroz - 3);
        skipPenalty = true;
        skipLegendEffect = true;
        workingState = {
          ...workingState,
          activeEvent: {
            title: '¡Protección!',
            message: 'El arroz con leche te protegió de la maldición.',
            icon: '🍮',
            type: 'GOOD'
          }
        };
        setGameState(workingState);
        broadcastState(workingState);
        await waitForInteraction();
      }
      window._lastChoice = null;
      syncState();
    }

    if (!skipLegendEffect && legendToResolve === MONA_LEGEND_ID) {
      const penalty = await handleMonaCurse(currentPlayerIdx);
      loseTamales(penalty, true);
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'tulevieja') {
      workingState = {
        ...workingState,
        activeChoice: {
          playerIdx: currentPlayerIdx,
          type: 'TULEVIEJA',
          title: '¡La Tulevieja!',
          message: '¿Pagas 3 tamales para quedarte o retrocedes 3 casillas?',
          options: [
            { label: 'Pagar 3 tamales', value: 'PAY', icon: '🧾', cost: 3 },
            { label: 'Retroceder 3', value: 'BACK', icon: '⬅️' }
          ]
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      const decision = window._lastChoice;
      window._lastChoice = null;
      if (decision === 'PAY') {
        loseTamales(3, false);
        skipPenalty = true;
        syncState();
      } else {
        skipPenalty = true;
        let backPos = Math.max(0, finalPos - 3);
        while (currentPos !== backPos) {
          currentPos--;
          const stepPlayers = [...workingState.players];
          stepPlayers[currentPlayerIdx] = { ...stepPlayers[currentPlayerIdx], position: currentPos };
          setGameState((s) => ({ ...s, players: stepPlayers }));
          await sleep(200);
        }
        finalPos = backPos;
        syncState();
      }
    } else if (!skipLegendEffect && newInventory.includes('Cafe')) {
      workingState = {
        ...workingState,
        activeChoice: {
          playerIdx: currentPlayerIdx,
          type: 'CAFE',
          title: '¡Cafe!',
          message: 'Puedes retroceder 1 casilla para evitar la maldicion.',
          options: [
            { label: 'Usar Cafe', value: 'USE', icon: '☕' },
            { label: 'Guardar', value: 'KEEP', icon: '🧳' }
          ]
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      const decision = window._lastChoice;
      window._lastChoice = null;
      if (decision === 'USE') {
        newInventory = newInventory.filter((i) => i !== 'Cafe');
        skipLegendEffect = true;
        const backPos = Math.max(0, finalPos - 1);
        while (currentPos !== backPos) {
          currentPos--;
          const stepPlayers = [...workingState.players];
          stepPlayers[currentPlayerIdx] = { ...stepPlayers[currentPlayerIdx], position: currentPos };
          setGameState((s) => ({ ...s, players: stepPlayers }));
          await sleep(200);
        }
        finalPos = backPos;
        workingState = {
          ...workingState,
          activeEvent: {
            title: '¡Cafe bien cargado!',
            message: 'Retrocedes 1 casilla y evitas la maldicion.',
            icon: '☕',
            type: 'GOOD'
          }
        };
        setGameState(workingState);
        broadcastState(workingState);
        await waitForInteraction();
        syncState();
      } else {
        syncState();
      }
    }
    if (!skipLegendEffect && legendToResolve === 'los_duendes' && newInventory.includes('Cajetas de Leche')) {
      newInventory = newInventory.filter((i) => i !== 'Cajetas de Leche');
      skipLegendEffect = true;
      workingState = {
        ...workingState,
        activeEvent: {
          title: '¡Cajetas de Leche!',
          message: 'Endulzaste a los duendes. Se consumio la cajeta.',
          icon: '🍬',
          type: 'GOOD'
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    }
    if (!skipLegendEffect && legendToResolve === 'mico_malo') {
      loseTamales(5, true);
      workingState = {
        ...workingState,
        activeEvent: {
          title: '¡El Mico Malo!',
          message: 'Pierdes 5 tamales.',
          icon: '/mico.webp',
          type: 'BAD'
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'la_segua') {
      const quiz = COSTARRICANISMOS[Math.floor(Math.random() * COSTARRICANISMOS.length)];
      const opts = [
        { label: quiz.meaning, value: 'OK', icon: '❓' },
        { label: 'Algo malo', value: 'NO', icon: '❓' },
        { label: 'Nada que ver', value: 'NO2', icon: '❓' }
      ].sort(() => 0.5 - Math.random());
      workingState = {
        ...workingState,
        activeChoice: {
          playerIdx: currentPlayerIdx,
          type: 'DUENDES',
          title: '¡Reto de La Segua!',
          message: `¿Qué significa "${quiz.word}"?`,
          options: opts
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      if (window._lastChoice === 'OK') {
        finalTamales += 5;
      } else {
        loseTamales(5, true);
      }
      window._lastChoice = null;
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'carreta_sin_bueyes') {
      const targets = workingState.players.filter((p, idx) => idx !== currentPlayerIdx && !p.isFinished && p.tamales > 0);
      if (targets.length) {
        workingState = {
          ...workingState,
          activeChoice: {
            playerIdx: currentPlayerIdx,
            type: 'CARRETA',
            title: '¡La Carreta!',
            message: 'Elige a quien arrastrar 1 casilla y quitarle 1 tamal.',
            options: targets.map((p) => ({ label: p.name, value: String(p.id), icon: '🪵' }))
          }
        };
        setGameState(workingState);
        broadcastState(workingState);
        await waitForInteraction();
        const chosenId = window._lastChoice;
        window._lastChoice = null;
        if (chosenId != null) {
          const targetId = String(chosenId);
          const updatedPlayers = workingState.players.map((p) => {
            if (String(p.id) !== targetId) return p;
            const newPos = Math.max(0, p.position - 1);
            return { ...p, tamales: Math.max(0, p.tamales - 1), position: newPos };
          });
          workingState = { ...workingState, players: updatedPlayers };
          setGameState(workingState);
          broadcastState(workingState);
        }
        syncState();
      }
      loseTamales(2, true);
      let backPos = Math.max(0, finalPos - 2);
      while (currentPos !== backPos) {
        currentPos--;
        const stepPlayers = [...workingState.players];
        stepPlayers[currentPlayerIdx] = { ...stepPlayers[currentPlayerIdx], position: currentPos };
        setGameState((s) => ({ ...s, players: stepPlayers }));
        await sleep(200);
      }
      finalPos = backPos;
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'los_duendes') {
      setUiState({ showTargetSelector: { itemId: 'Duendes' } });
      await waitForInteraction();
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'dueno_monte') {
      const loss = await handleDuenoPenalty();
      loseTamales(loss, true);
      workingState = { ...workingState, activeEvent: { title: '¡Castigo!', message: `Perdiste ${loss} tamales.`, icon: '🌲', type: 'BAD' } };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'sanatorio_duran') {
      skipPenalty = true;
      const randomPrevPos = Math.floor(Math.random() * finalPos);
      workingState = { ...workingState, activeEvent: { title: '¡Fantasmas!', message: `¡El Sanatorio te desoriento! Retrocedes a la casilla ${randomPrevPos + 1}.`, icon: '/duran.webp', type: 'BAD' } };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      finalPos = randomPrevPos;
      const stepPlayers = [...workingState.players];
      stepPlayers[currentPlayerIdx] = { ...stepPlayers[currentPlayerIdx], position: finalPos };
      workingState = { ...workingState, players: stepPlayers };
      setGameState(workingState);
      broadcastState(workingState);
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'la_llorona') {
      setUiState({ showTargetSelector: { itemId: 'Llorona' } });
      await waitForInteraction();
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'hormigas_nandayure') {
      if (newInventory.includes('Algodón de Azúcar')) {
        workingState = {
          ...workingState,
          activeChoice: {
            playerIdx: currentPlayerIdx,
            type: 'ALGODON_HORMIGAS',
            title: 'Algodón de Azúcar',
            message: '¿Quieres usarlo para evitar la maldición de las hormigas?',
            options: [
              { label: 'Usar Algodón', value: 'USE', icon: '🍭' },
              { label: 'Recibir castigo', value: 'TAKE', icon: '🐜' }
            ]
          }
        };
        setGameState(workingState);
        broadcastState(workingState);
        await waitForInteraction();
        if (window._lastChoice === 'USE') {
          newInventory = newInventory.filter((i) => i !== 'Algodón de Azúcar');
          skipLegendEffect = true;
          workingState = {
            ...workingState,
            activeEvent: {
              title: '¡Dulce protección!',
              message: 'El algodón evitó la maldición de las hormigas.',
              icon: '🍭',
              type: 'GOOD'
            }
          };
          setGameState(workingState);
          broadcastState(workingState);
          await waitForInteraction();
        }
        window._lastChoice = null;
        syncState();
      }
    }

    if (!skipLegendEffect && legendToResolve === 'hormigas_nandayure') {
      loseTamales(2, true);
      const playersWithLoss = workingState.players.map((p, idx) =>
        idx === currentPlayerIdx
          ? { ...p, tamales: finalTamales }
          : { ...p, tamales: Math.max(0, p.tamales - 1) }
      );
      workingState = {
        ...workingState,
        players: playersWithLoss,
        activeEvent: {
          title: 'A­Hormigas!',
          message: 'A­Te picaron las hormigas! Pierdes 2 tamales y todos los demA­s pierden 1.',
          icon: LEGENDS_DATA.hormigas_nandayure?.icon || '/hormiga.webp',
          type: 'BAD'
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'padre_sin_cabeza') {
      workingState = {
        ...workingState,
        activeChoice: {
          playerIdx: currentPlayerIdx,
          type: 'PADRE',
          title: 'El Padre sin Cabeza',
          message: 'Paga 3 tamales o bloquea la meta por 2 turnos.',
          options: [
            { label: 'Pagar 3 tamales', value: 'PAY', icon: '⛪', cost: 3 },
            { label: 'Bloquear meta', value: 'BLOCK', icon: '⛓️' }
          ]
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
      const decision = window._lastChoice;
      window._lastChoice = null;
      if (decision === 'PAY') {
        loseTamales(3, false);
        workingState = {
          ...workingState,
          activeChoice: null,
          activeEvent: {
            title: '¡Ofrenda Pagada!',
            message: 'Pagaste 3 tamales para evitar el bloqueo.',
            icon: '⛪',
            type: 'GOOD'
          }
        };
      } else {
        workingState = {
          ...workingState,
          endBlockedTurns: 2,
          activeChoice: null,
          activeEvent: {
            title: '¡Meta Bloqueada!',
            message: 'La meta queda bloqueada por 2 turnos.',
            icon: '⛓️',
            type: 'BAD'
          }
        };
        endBlockJustSet = true;
      }
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'iztaru') {
      const otherPlayers = workingState.players.filter((p, idx) => idx !== currentPlayerIdx && !p.isFinished && p.tamales > 0);
      if (otherPlayers.length > 0) {
        const minPos = Math.min(...otherPlayers.map((p) => p.position));
        const lastPlayer = otherPlayers.find((p) => p.position === minPos);
        if (lastPlayer && lastPlayer.position < finalPos) {
          const currentPosAtLanding = finalPos;
          const lastPlayerPos = lastPlayer.position;
          const swappedPlayers = workingState.players.map((p, idx) => {
            if (idx === currentPlayerIdx) return { ...p, position: lastPlayerPos };
            if (p.id === lastPlayer.id) return { ...p, position: currentPosAtLanding };
            return p;
          });
          finalPos = lastPlayerPos;
          workingState = { ...workingState, players: swappedPlayers, activeEvent: { title: '¡Pasajera Fantasmal!', message: `La pasajera desaparece y te cambia el destino con ${lastPlayer.name}.`, icon: '👻', type: 'NEUTRAL' } };
          setGameState(workingState);
          broadcastState(workingState);
          await waitForInteraction();
          syncState();
        }
      }
    } else if (!skipLegendEffect && legendToResolve === 'ninguenta') {
      finalTamales += 7;
      workingState = {
        ...workingState,
        activeEvent: {
          title: '¡La Nigüenta!',
          message: 'La buena suerte te favorece. Recibes 7 tamales.',
          icon: '/ninguenta.webp',
          type: 'GOOD'
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'rio_oro') {
      const riddle = ADIVINANZAS[Math.floor(Math.random() * ADIVINANZAS.length)];
      const voters = [workingState.players[currentPlayerIdx]?.id].filter((id) => !!id);
      const options = shuffleArray(riddle.options);
      const rioOroRiddle = {
        riddle: `Reto grupal: ${riddle.riddle}`,
        options,
        correctAnswer: riddle.answer,
        voters,
        answers: {},
        playerIdx: currentPlayerIdx
      };
      workingState = {
        ...workingState,
        rioOroRiddle,
        activeChoice: buildRioOroChoice(rioOroRiddle)
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      const result = window._lastChoice;
      window._lastChoice = null;
      if (result && result.type === 'RIO_ORO' && !result.success) {
        let animatedPlayers = [...workingState.players];
        for (let step = 0; step < 2; step++) {
          animatedPlayers = animatedPlayers.map((p) => {
            if (p.isFinished || p.tamales <= 0) return p;
            return { ...p, position: Math.max(0, p.position - 1) };
          });
          workingState = { ...workingState, players: animatedPlayers };
          setGameState(workingState);
          await sleep(200);
        }
        broadcastState(workingState);
        finalPos = animatedPlayers[currentPlayerIdx]?.position ?? finalPos;
        currentPos = finalPos;
      }
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'copito') {
      finalTamales += 2;
      const giftCandidates = workingState.players.filter((p, idx) => idx !== currentPlayerIdx && !p.isFinished);
      if (giftCandidates.length) {
        workingState = {
          ...workingState,
          activeChoice: {
            playerIdx: currentPlayerIdx,
            type: 'COPITO_TARGET',
            title: 'El Copito',
            message: 'Ganas 2 tamales. Elige a quien darle 1 tamal.',
            options: giftCandidates.map((p) => ({
              label: p.name,
              value: String(p.id),
              icon: '/copito.webp'
            }))
          }
        };
        setGameState(workingState);
        broadcastState(workingState);
        await waitForInteraction();
        const targetId = Number(window._lastChoice);
        window._lastChoice = null;
        if (!Number.isNaN(targetId)) {
          finalTamales = Math.max(0, finalTamales - 1);
          workingState = {
            ...workingState,
            players: workingState.players.map((p, idx) => {
              if (idx === currentPlayerIdx) return { ...p, tamales: finalTamales };
              if (p.id === targetId) return { ...p, tamales: p.tamales + 1 };
              return p;
            })
          };
          setGameState(workingState);
          broadcastState(workingState);
        }
      }
      workingState = {
        ...workingState,
        activeChoice: {
          playerIdx: currentPlayerIdx,
          type: 'COPITO_MOVE',
          title: 'El Copito',
          message: 'Elige moverte 1 casilla, retroceder o quedarte.',
          options: [
            { label: 'Adelante', value: 'FORWARD', icon: '⬆️' },
            { label: 'Quedarme', value: 'STAY', icon: '•' },
            { label: 'Atras', value: 'BACK', icon: '⬇️' }
          ]
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      const moveChoice = window._lastChoice;
      window._lastChoice = null;
      if (moveChoice === 'FORWARD') {
        const nextPos = Math.min(TOTAL_TILES - 1, finalPos + 1);
        if (nextPos !== finalPos) {
          currentPos = finalPos;
          while (currentPos !== nextPos) {
            currentPos++;
            const stepPlayers = [...workingState.players];
            stepPlayers[currentPlayerIdx] = { ...stepPlayers[currentPlayerIdx], position: currentPos };
            setGameState((s) => ({ ...s, players: stepPlayers }));
            await sleep(200);
          }
          finalPos = nextPos;
        }
      } else if (moveChoice === 'BACK') {
        const nextPos = Math.max(0, finalPos - 1);
        if (nextPos !== finalPos) {
          currentPos = finalPos;
          while (currentPos !== nextPos) {
            currentPos--;
            const stepPlayers = [...workingState.players];
            stepPlayers[currentPlayerIdx] = { ...stepPlayers[currentPlayerIdx], position: currentPos };
            setGameState((s) => ({ ...s, players: stepPlayers }));
            await sleep(200);
          }
          finalPos = nextPos;
        }
      }
      syncState();
    } else if (!skipLegendEffect && legendToResolve === 'oso_caballo') {
      if (finalTamales >= 5) {
        workingState = {
          ...workingState,
          activeChoice: {
            playerIdx: currentPlayerIdx,
            type: 'OSO_CABALLO',
            title: 'Oso Caballo',
            message: 'Puedes pagar 5 tamales o todos regresan a la casilla 1.',
            options: [
              { label: 'Pagar 5 tamales', value: 'PAY', icon: '/oso.webp', cost: 5 },
              { label: 'No pagar', value: 'RESET', icon: '🔁' }
            ]
          }
        };
        setGameState(workingState);
        broadcastState(workingState);
        await waitForInteraction();
        const decision = window._lastChoice;
        window._lastChoice = null;
        if (decision === 'PAY') {
          loseTamales(5, false);
          workingState = {
            ...workingState,
            activeChoice: null,
            activeEvent: {
              title: 'Oso Caballo',
              message: 'Pagaste 5 tamales y evitas reiniciar la ruta.',
              icon: '/oso.webp',
              type: 'GOOD'
            }
          };
          setGameState(workingState);
          broadcastState(workingState);
          await waitForInteraction();
          syncState();
        } else {
          const resetPlayers = workingState.players.map((p) => ({
            ...p,
            position: 0,
            isFinished: false
          }));
          finalPos = 0;
          currentPos = 0;
          workingState = {
            ...workingState,
            players: resetPlayers,
            finishCount: 0,
            winner: null,
            activeChoice: null,
            activeEvent: {
              title: 'Oso Caballo',
              message: 'El bosque se enfurece. Todos regresan a la casilla de inicio.',
              icon: '/oso.webp',
              type: 'BAD'
            }
          };
          setGameState(workingState);
          broadcastState(workingState);
          await waitForInteraction();
          syncState();
        }
      } else {
        const resetPlayers = workingState.players.map((p) => ({
          ...p,
          position: 0,
          isFinished: false
        }));
        finalPos = 0;
        currentPos = 0;
        workingState = {
          ...workingState,
          players: resetPlayers,
          finishCount: 0,
          winner: null,
          activeEvent: {
            title: 'Oso Caballo',
          message: 'No tienes 5 tamales. Todos regresan a la casilla de inicio.',
            icon: '/oso.webp',
            type: 'BAD'
          }
        };
        setGameState(workingState);
        broadcastState(workingState);
        await waitForInteraction();
        syncState();
      }
    } else if (!skipLegendEffect && legendToResolve === 'isla_san_lucas') {
      const voters = workingState.players.filter((p) => !p.isFinished && p.tamales > 0).map((p) => p.id);
      const sanLucasVote = { voters, votes: {}, candidates: voters };
      workingState = {
        ...workingState,
        sanLucasVote,
        activeChoice: buildSanLucasVoteChoice(sanLucasVote)
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    }

    if (!skipLegendEffect && legendToResolve === 'carreta_sin_bueyes' && !skipPenalty) {
      // handled above
    }
  }

  if (!skipTileEffects && tile.type === TileType.PENALTY && !skipPenalty) {
    const penaltyBack = 2;
    finalPos = Math.max(0, finalPos - penaltyBack);
    while (currentPos !== finalPos) {
      currentPos--;
      const stepPlayers = [...workingState.players];
      stepPlayers[currentPlayerIdx] = { ...stepPlayers[currentPlayerIdx], position: currentPos };
      setGameState((s) => ({ ...s, players: stepPlayers }));
      await sleep(200);
    }
  }

  if (!skipTileEffects && tile.type === TileType.PACT) {
    const partner = workingState.players.find((p, idx) => idx !== currentPlayerIdx && !p.isFinished && p.tamales > 0 && p.position === finalPos);
    if (partner) {
      const updatedPlayers = workingState.players.map((p, idx) => {
        if (idx === currentPlayerIdx) return { ...p, pactPartnerId: partner.id, pactTurns: 2 };
        if (p.id === partner.id) return { ...p, pactPartnerId: workingState.players[currentPlayerIdx].id, pactTurns: 2 };
        return p;
      });
      workingState = {
        ...workingState,
        players: updatedPlayers,
        activeEvent: {
          title: 'A?Pacto del Jocote!',
          message: `Quedaste aliado con ${partner.name} por 2 turnos. Si uno pierde tamales por maldicion, el otro pierde la mitad.`,
          icon: '/jocote.webp',
          type: 'GOOD'
        }
      };
    } else {
      workingState = {
        ...workingState,
        activeEvent: {
          title: 'A?Pacto del Jocote!',
          message: 'Si otro jugador cae aqui, se activara el pacto.',
          icon: '/jocote.webp',
          type: 'NEUTRAL'
        }
      };
    }
    setGameState(workingState);
    broadcastState(workingState);
    await waitForInteraction();
    syncState();
  }

  if (!skipTileEffects && tile.type === TileType.NORMAL && !tile.legendId) {
    if (tile.isPuertoViejo) {
      newInventory.push('Pati Picante');
      workingState = {
        ...workingState,
        activeEvent: {
          title: '¡Puerto Viejo!',
          message: 'Ganas un Pati Picante.',
          icon: '/puerto_viejo.webp',
          type: 'GOOD'
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    } else {
      finalArroz += 1;
      workingState = {
        ...workingState,
        activeEvent: {
          title: '¡Arroz con Leche!',
          message: 'Encuentras un arroz con leche en la provincia.',
          icon: '🍮',
          type: 'GOOD'
        }
      };
      setGameState(workingState);
      broadcastState(workingState);
      await waitForInteraction();
      syncState();
    }
  }

  let finishBonus = 0;
  let newFinishCount = workingState.finishCount;
  if (!skipTileEffects && tile.type === TileType.END) {
    newFinishCount++;
    switch (newFinishCount) {
      case 1:
        finishBonus = 4;
        break;
      case 2:
        finishBonus = 3;
        break;
      case 3:
        finishBonus = 2;
      default:
        finishBonus = 1;
        break;
    }
    finalTamales += finishBonus;
    workingState = {
      ...workingState,
      finishCount: newFinishCount,
      activeEvent: {
        title: '¡Meta Llegada!',
        message: `¡Llegaste a San José en el puesto #${newFinishCount}! Ganas ${finishBonus} tamales de regalo.`,
        icon: '🏙️',
        type: 'GOOD'
      }
    };
    setGameState(workingState);
    broadcastState(workingState);
    await waitForInteraction();
    syncState();
  }

  const comment = await getSpookyComment(tile.type, currentPlayer.name);
  const finalPlayers = [...workingState.players];
  const isFinished = tile.type === TileType.END;
  if (finalTamales <= 0 && newInventory.includes('Suspiros Costarricenses')) {
    const suspiroIndex = newInventory.indexOf('Suspiros Costarricenses');
    if (suspiroIndex >= 0) {
      newInventory = newInventory.filter((_, idx) => idx !== suspiroIndex);
    }
    finalTamales = 2;
    workingState = {
      ...workingState,
      activeEvent: {
        title: 'Suspiros Costarricenses',
        message: 'Los suspiros te devuelven la vida. Revives con 2 tamales.',
        icon: getItemIcon('Suspiros Costarricenses'),
        type: 'GOOD'
      }
    };
    setGameState(workingState);
    broadcastState(workingState);
    await waitForInteraction();
    syncState();
  }
  const isDead = finalTamales <= 0;
  const currentPactTurns = finalPlayers[currentPlayerIdx]?.pactTurns || 0;
  const nextPactTurns = currentPactTurns > 0 ? currentPactTurns - 1 : 0;
  const nextPactPartnerId = nextPactTurns === 0 ? null : finalPlayers[currentPlayerIdx]?.pactPartnerId;
  const currentSlowTurns = finalPlayers[currentPlayerIdx]?.slowTurns || 0;
  const nextSlowTurns = currentSlowTurns > 0 ? currentSlowTurns - 1 : 0;

  finalPlayers[currentPlayerIdx] = {
    ...finalPlayers[currentPlayerIdx],
    position: finalPos,
    tamales: finalTamales,
    inventory: newInventory,
    isDrunk: false,
    isFinished: isFinished || isDead,
    arroz: finalArroz,
    slowTurns: nextSlowTurns,
    pactPartnerId: nextPactPartnerId,
    pactTurns: nextPactTurns
  };

  if (isDead) {
    workingState = { ...workingState, activeEvent: { title: '¡Fuera!', message: `¡${currentPlayer.name} se quedó sin tamales y ha muerto en la ruta!`, icon: '💀', type: 'BAD' } };
    setGameState(workingState);
    broadcastState(workingState);
    await waitForInteraction();
    syncState();
  }

  const gameEnded = finalPlayers.every((p) => p.isFinished || p.tamales <= 0);
  let ultimateWinner = null;
  if (gameEnded) {
    finalPlayers.forEach((p, idx) => {
      const bonus = Math.floor((p.arroz || 0) / 2);
      if (bonus > 0) {
        finalPlayers[idx] = { ...p, tamales: p.tamales + bonus, arroz: 0 };
      } else {
        finalPlayers[idx] = { ...p, arroz: 0 };
      }
    });
    ultimateWinner = finalPlayers.reduce((prev, current) => (prev.tamales > current.tamales ? prev : current));
  } else if (!skipTileEffects && tile.type === TileType.FAIR) {
    const prizeRoll = Math.random();
    let item = '';
    let ev;
    if (prizeRoll < 0.18) {
      item = 'Caldosa';
      ev = { title: 'Feria del Agricultor', message: 'Ganas una Caldosa para compartir.', icon: getItemIcon('Caldosa'), type: 'GOOD' };
    } else if (prizeRoll < 0.36) {
      item = 'Cajetas de Leche';
      ev = { title: 'Feria del Agricultor', message: 'Ganas Cajetas de Leche.', icon: getItemIcon('Cajetas de Leche'), type: 'GOOD' };
    } else if (prizeRoll < 0.54) {
      item = 'Cafe';
      ev = { title: 'Feria del Agricultor', message: 'Ganas un Cafe para mantenerte despierto.', icon: getItemIcon('Cafe'), type: 'GOOD' };
    } else if (prizeRoll < 0.72) {
      item = 'Leche Dormida';
      ev = { title: 'Feria del Agricultor', message: 'Ganas una Leche Dormida para adormecer a un rival.', icon: getItemIcon('Leche Dormida'), type: 'GOOD' };
    } else if (prizeRoll < 0.90) {
      item = 'Sopa de Mondongo';
      ev = { title: 'Feria del Agricultor', message: 'Ganas una Sopa de Mondongo.', icon: getItemIcon('Sopa de Mondongo'), type: 'GOOD' };
    } else {
      item = 'Suspiros Costarricenses';
      ev = { title: 'Feria del Agricultor', message: 'Ganas Suspiros Costarricenses.', icon: getItemIcon('Suspiros Costarricenses'), type: 'GOOD' };
    }
    if (item) newInventory.push(item);
    workingState = { ...workingState, activeEvent: ev };
    setGameState(workingState);
    broadcastState(workingState);
    await waitForInteraction();
    syncState();
  }

  let remainingEndBlockedTurns = workingState.endBlockedTurns || 0;
  if (!endBlockJustSet && remainingEndBlockedTurns > 0) remainingEndBlockedTurns -= 1;

  const currentFinal = finalPlayers[currentPlayerIdx];
  const boardSnapshot = getBoard();
  const currentTile = currentFinal ? boardSnapshot[currentFinal.position] : null;
  const positionsSummary = currentFinal && currentTile ? `${currentFinal.name} en ${currentTile.label}` : '';
  const finalState = {
    ...workingState,
    players: finalPlayers,
    isRolling: false,
    narratorComment: positionsSummary ? `${comment} Posicion: ${positionsSummary}` : comment,
    winner: ultimateWinner,
    currentPlayerIndex: (workingState.currentPlayerIndex + 1) % workingState.players.length,
    activeEvent: null,
    activeChoice: null,
    endBlockedTurns: remainingEndBlockedTurns
  };
  setGameState(finalState);
  broadcastState(finalState);
}
async function processItemUsage(itemId, targetId, fromId, destinationPos, stolenItemId, removeLegendId, shareTargetId) {
  const from = gameState.players.find((p) => p.id === fromId);
  const target = gameState.players.find((p) => p.id === targetId);
  if (!from) return;

  if (itemId === 'Duendes') {
    const riddle = ADIVINANZAS[Math.floor(Math.random() * ADIVINANZAS.length)];
    const opts = riddle.options
      .map((o) => ({ label: o, value: o === riddle.answer ? 'YES' : 'NO', icon: '❓' }))
      .sort(() => 0.5 - Math.random());

    const duelChoice = {
      playerIdx: gameState.players.indexOf(target),
      targetId: target?.id,
      type: 'DUENDES',
      title: '¡Duelo de Duendes!',
      message: `${from.name} te reta: "${riddle.riddle}"`,
      options: opts
    };

    setGameState((prev) => {
      const s = { ...prev, activeChoice: duelChoice };
      broadcastState(s);
      return s;
    });

    const checker = setInterval(() => {
      const decision = window._lastChoice;
      if (!decision) return;
      clearInterval(checker);
      window._lastChoice = null;

      setGameState((current) => {
        let attTam = from.tamales;
        let tarTam = target.tamales;
        let ev;
        if (decision === 'YES') {
          attTam = Math.max(0, from.tamales - 2);
          ev = { title: '¡Adivinó!', message: `${target.name} lo resolvió. ${from.name} pierde 2 tamales.`, icon: '👺', type: 'BAD' };
        } else {
          tarTam = Math.max(0, target.tamales - 2);
          ev = { title: '¡Falló!', message: `${target.name} no pudo. Pierde 2 tamales.`, icon: '👺', type: 'GOOD' };
        }

        const s = {
          ...current,
          players: current.players.map((p) =>
            p.id === fromId ? { ...p, tamales: attTam } : p.id === targetId ? { ...p, tamales: tarTam } : p
          ),
          activeChoice: null,
          activeEvent: ev
        };
        broadcastState(s);
        return s;
      });
    }, 500);
    return;
  }

  if (itemId === 'Algodón de Azúcar') {
    const playerIdx = gameState.players.indexOf(from);
    const algodChoice = {
      playerIdx,
      type: 'ALGODON',
      title: 'Algodón de Azúcar',
      message: '¿Quieres convertirlo en 2 arroz con leche?',
      options: [
        { label: 'Convertir (+2)', value: 'CONVERT', icon: '🍭' },
        { label: 'Guardar', value: 'KEEP', icon: '💾' }
      ]
    };

    setGameState((prev) => {
      const s = { ...prev, activeChoice: algodChoice };
      broadcastState(s);
      return s;
    });

    const checker = setInterval(() => {
      const decision = window._lastChoice;
      if (!decision) return;
      clearInterval(checker);
      window._lastChoice = null;

      setGameState((current) => {
        if (decision !== 'CONVERT') {
          const s = { ...current, activeChoice: null };
          broadcastState(s);
          return s;
        }

        const newPlayers = current.players.map((p) => {
          if (p.id !== fromId) return p;
          const newInv = p.inventory.filter((i) => i !== 'Algodón de Azúcar');
          return { ...p, inventory: newInv, arroz: (p.arroz || 0) + 2 };
        });
        const s = {
          ...current,
          players: newPlayers,
          activeChoice: null,
          activeEvent: { title: '¡Dulce!', message: 'Recibes 2 arroz con leche.', icon: '🍮', type: 'GOOD' }
        };
        broadcastState(s);
        return s;
      });
    }, 500);
    return;
  }

  if (itemId === 'Sanatorio') {
    const prevPos = Math.floor(Math.random() * target.position);
    const s = {
      ...gameState,
      players: gameState.players.map((p) =>
        p.id === targetId
          ? { ...p, position: prevPos }
          : p.id === fromId
            ? { ...p, inventory: p.inventory.filter((i) => i !== 'Sanatorio') }
            : p
      ),
      narratorComment: `¡${from.name} usó el Sanatorio! ${target.name} retrocede a la casilla ${prevPos + 1}.`,
      gameLog: [`${from.name} desorientó a ${target.name} al Sanatorio.`, ...gameState.gameLog].slice(0, 8)
    };
    setGameState(s);
    broadcastState(s);
    return;
  }

  if (itemId === 'Gallo Pinto' && removeLegendId) {
    const board = getBoard();
    const updatedBoard = board.map((tile) => {
      if (tile.legendId === removeLegendId) {
        return {
          ...tile,
          type: TileType.NORMAL,
          legendId: undefined,
          label: 'Puerto Viejo',
          description: 'Pueblo del Caribe costarricense.',
          icon: '/puerto_viejo.webp',
          isPuertoViejo: true
        };
      }
      return tile;
    });
    const s = {
      ...gameState,
      boardPath: updatedBoard,
      players: gameState.players.map((p) =>
        p.id === fromId ? { ...p, inventory: p.inventory.filter((i) => i !== 'Gallo Pinto') } : p
      ),
      narratorComment: `¡${from.name} eliminó una leyenda del tablero!`
    };
    setGameState(s);
    broadcastState(s);
    return;
  }

  if (!target) return;

  const patiTransfer = itemId === 'Pati Picante' && (target.arroz || 0) > 0 ? 1 : 0;
  const mondongoSteal = itemId === 'Sopa de Mondongo' ? Math.min(1, target.tamales) : 0;

  const newPlayers = gameState.players.map((p) => {
    if (p.id === fromId) {
      let newInv = p.inventory.filter((i) => i !== itemId);
      if (stolenItemId) newInv.push(stolenItemId);
      if (itemId === 'Pati Picante') {
        return { ...p, inventory: newInv, arroz: (p.arroz || 0) + patiTransfer };
      }
      if (itemId === 'Sopa de Mondongo') {
        return { ...p, inventory: newInv, tamales: p.tamales + mondongoSteal };
      }
      return { ...p, inventory: newInv };
      if (itemId === 'Suspiros Costarricenses') {
        if (p.tamales <= 0) {
          return { ...p, tamales: 2, isFinished: false };
        }
        return p;
      }
    }
    if (p.id === targetId) {
      if (itemId === 'Chifrijo') return { ...p, tamales: Math.max(0, p.tamales - 2) };
      if (itemId === 'Trago de Cacique') return { ...p, isSkipped: true, skipSourceName: from?.name || null };
      if (itemId === 'Casado' && destinationPos !== undefined) return { ...p, position: destinationPos };
      if (itemId === 'Llorona') return { ...p, tamales: p.tamales + Math.floor(from.tamales / 2) };
      if (itemId === 'Rice and Beans' && stolenItemId) {
        return { ...p, inventory: p.inventory.filter((i) => i !== stolenItemId) };
      }
      if (itemId === 'Caldosa') {
        const stolen = Math.min(2, p.tamales);
        return { ...p, tamales: Math.max(0, p.tamales - stolen) };
      }
      if (itemId === 'Leche Dormida') {
        return { ...p, slowTurns: 2 };
      }
      if (itemId === 'Pati Picante') {
        return { ...p, position: Math.max(0, p.position - 2), arroz: Math.max(0, (p.arroz || 0) - patiTransfer) };
      }
      if (itemId === 'Sopa de Mondongo') {
        return { ...p, tamales: Math.max(0, p.tamales - mondongoSteal), position: Math.max(0, p.position - 2) };
      }
      if (itemId === 'Suspiros Costarricenses') {
        if (p.tamales <= 0) {
          return { ...p, tamales: 2, isFinished: false };
        }
        return p;
      }
      return p;
    }
    if (itemId === 'Caldosa' && shareTargetId && p.id === shareTargetId) {
      return { ...p, tamales: p.tamales + 1 };
    }
    return p;
  });

  let caldosaGain = 0;
  let caldosaTargetName = '';
  let caldosaShareName = '';
  let caldosaStolen = 0;
  if (itemId === 'Caldosa') {
    const target = gameState.players.find((p) => p.id === targetId);
    caldosaTargetName = target?.name || '';
    caldosaStolen = target ? Math.min(2, target.tamales) : 0;
    const shared = shareTargetId ? Math.min(1, caldosaStolen) : 0;
    caldosaGain = Math.max(0, caldosaStolen - shared);
    if (shareTargetId) {
      const shareTarget = gameState.players.find((p) => p.id === shareTargetId);
      caldosaShareName = shareTarget?.name || '';
    }
  }
  let patiTargetName = '';
  if (itemId === 'Pati Picante') {
    patiTargetName = target?.name || 'un rival';
  }
  const finalPlayers = itemId === 'Caldosa'
    ? newPlayers.map((p) => (p.id === fromId ? { ...p, tamales: p.tamales + caldosaGain } : p))
    : newPlayers;

  const caldosaShareText = caldosaStolen >= 1
    ? `y comparte 1 con ${caldosaShareName || 'otro jugador'}.`
    : 'y no pudo compartir porque no se robaron tamales.';
  let finalState = {
    ...gameState,
    players: finalPlayers,
    activeEvent: itemId === "Caldosa"
      ? {
          title: "Caldosa compartida",
          message: `${from.name} roba ${caldosaStolen} a ${caldosaTargetName || "un rival"} ${caldosaShareText}`,
          icon: getItemIcon("Caldosa"),
          type: "GOOD"
        }
      : gameState.activeEvent,
    gameLog: [`${from.name} usA3 ${itemId} contra ${target?.name || "un rival"}`, ...gameState.gameLog].slice(0, 8)
  };
  if (itemId === "Leche Dormida") {
    finalState = {
      ...finalState,
      activeEvent: {
        title: "Leche Dormida",
        message: `${from.name} adormece a ${target?.name || "un rival"}. Solo avanzara 2 casillas por turno.`,
        icon: getItemIcon("Leche Dormida"),
        type: "GOOD"
      }
    };
  }
  if (itemId === "Chifrijo") {
    finalState = {
      ...finalState,
      activeEvent: {
        title: "Chifrijo",
        message: `${from.name} le quito 2 tamales a ${target?.name || "un rival"}.`,
        icon: getItemIcon("Chifrijo"),
        type: "BAD"
      }
    };
  }
  if (itemId === "Rice and Beans" && stolenItemId) {
    finalState = {
      ...finalState,
      activeEvent: {
        title: "Rice and Beans",
        message: `${from.name} le quito ${stolenItemId} a ${target?.name || "un rival"}.`,
        icon: getItemIcon("Rice and Beans"),
        type: "BAD"
      }
    };
  }
  if (itemId === "Suspiros Costarricenses") {
    finalState = {
      ...finalState,
      activeEvent: {
        title: "Suspiros Costarricenses",
        message: `${from.name} revive a ${target?.name || "un jugador"} con Suspiros. Vuelve con 2 tamales.`,
        icon: getItemIcon("Suspiros Costarricenses"),
        type: "GOOD"
      }
    };
  }
  if (itemId === "Sopa de Mondongo") {
    finalState = {
      ...finalState,
      activeEvent: {
        title: "Sopa de Mondongo",
        message: `${from.name} le tiro sopa de mondongo a ${target?.name || "un rival"}. Le quita 1 tamal y retrocede 2 casillas.`,
        icon: getItemIcon("Sopa de Mondongo"),
        type: "BAD"
      }
    };
  }
  if (itemId === "Pati Picante") {
    const patiTarget = patiTargetName || "un rival";
    finalState = {
      ...finalState,
      activeEvent: {
        title: "Pati Picante",
        message: patiTransfer
          ? `${from.name} le quito 1 arroz con leche a ${patiTarget} y lo hace retroceder 2 casillas.`
          : `${from.name} hace retroceder 2 casillas a ${patiTarget}.`,
        icon: getItemIcon("Pati Picante"),
        type: "GOOD"
      }
    };
  }
  setGameState(finalState);
  broadcastState(finalState);
}

function getMyPlayer() {
  return gameState.players.find(
    (p) => p.peerId === myId || (!gameState.roomCode && gameState.players.indexOf(p) === (gameState.status === 'playing' ? gameState.currentPlayerIndex : -1))
  );
}

function getCaldosaCandidates(excludeIds = []) {
  const excluded = new Set(excludeIds);
  return gameState.players.filter((p) => !excluded.has(p.id) && !p.isFinished && p.tamales > 0);
}

function isMyTurn() {
  const activeP = gameState.players[gameState.currentPlayerIndex];
  const remaining = gameState.players.filter((p) => !p.isFinished && p.tamales > 0);
  if (remaining.length === 1) {
    const last = remaining[0];
    return (last.peerId === myId) || (gameState.status === 'playing' && !gameState.roomCode);
  }
  return (activeP?.peerId === myId) || (gameState.status === 'playing' && !gameState.roomCode);
}

function useItemInitiator(targetId) {
  if (!uiState.showTargetSelector) return;
  const myPlayer = getMyPlayer();
  if (!myPlayer) return;

  if (uiState.showTargetSelector.itemId === 'Casado') {
    setUiState({ casadoTargetId: targetId });
  } else if (uiState.showTargetSelector.itemId === 'Rice and Beans') {
    const targetPlayer = gameState.players.find((p) => p.id === targetId);
    const hasItems = (targetPlayer?.inventory || []).length > 0;
    if (!hasItems) {
      if (gameState.isHost) {
        const s = {
          ...gameState,
          activeEvent: {
            title: 'A­Rice and Beans!',
            message: `${targetPlayer?.name || 'Ese jugador'} no tiene objetos o comida para intercambiar.`,
            icon: 'dY?s',
            type: 'NEUTRAL'
          }
        };
        setGameState(s);
        broadcastState(s);
      } else {
        showUiAlert('Rice and Beans', `${targetPlayer?.name || 'Ese jugador'} no tiene objetos o comida para intercambiar.`, 'NEUTRAL');
      }
      setUiState({ showTargetSelector: null });
      return;
    }
    setUiState({ stealTargetId: targetId });
  } else if (uiState.showTargetSelector.itemId === 'Caldosa') {
    const shareCandidates = getCaldosaCandidates([myPlayer.id, targetId]);
      if (!shareCandidates.length) {
        if (gameState.isHost) {
          const s = {
            ...gameState,
            activeEvent: {
              title: '¡Caldosa!',
              message: 'Necesitas otro jugador para compartir 1 tamal.',
              icon: '🥣',
              type: 'NEUTRAL'
            }
          };
          setGameState(s);
          broadcastState(s);
        } else {
          showUiAlert('Caldosa', 'Necesitas otro jugador para compartir 1 tamal.', 'NEUTRAL');
        }
        setUiState({ showTargetSelector: null, caldosaStealTargetId: null, caldosaShareTargetId: null });
        return;
      }
    setUiState({ showTargetSelector: null, caldosaStealTargetId: targetId, caldosaShareTargetId: true });
  } else {
    processItemUsage(uiState.showTargetSelector.itemId, targetId, myPlayer.id);
    setUiState({ showTargetSelector: null });
  }
}

function selectCasadoDestination(tileIndex) {
  const myPlayer = getMyPlayer();
  if (!myPlayer || uiState.casadoTargetId == null) return;
  processItemUsage('Casado', uiState.casadoTargetId, myPlayer.id, tileIndex);
  setUiState({ showTargetSelector: null, casadoTargetId: null });
}

function selectStolenItem(stolenItem) {
  const myPlayer = getMyPlayer();
  if (!myPlayer || uiState.stealTargetId == null) return;
  processItemUsage('Rice and Beans', uiState.stealTargetId, myPlayer.id, undefined, stolenItem);
  setUiState({ showTargetSelector: null, stealTargetId: null });
}

function selectCaldosaShareTarget(shareTargetId) {
  const myPlayer = getMyPlayer();
  if (!myPlayer || uiState.caldosaStealTargetId == null) return;
  if (gameState.isHost) {
    processItemUsage('Caldosa', uiState.caldosaStealTargetId, myPlayer.id, undefined, undefined, undefined, shareTargetId);
  } else {
    connections[0]?.send({
      type: 'USE_ITEM',
      itemId: 'Caldosa',
      targetId: uiState.caldosaStealTargetId,
      fromId: myPlayer.id,
      shareTargetId
    });
  }
  setUiState({ showTargetSelector: null, caldosaStealTargetId: null, caldosaShareTargetId: null });
}

function selectLegendToRemove(legendId) {
  const myPlayer = getMyPlayer();
  if (!myPlayer) return;
  if (gameState.isHost) {
    processItemUsage('Gallo Pinto', myPlayer.id, myPlayer.id, undefined, undefined, legendId);
  } else {
    connections[0]?.send({
      type: 'USE_ITEM',
      itemId: 'Gallo Pinto',
      targetId: myPlayer.id,
      fromId: myPlayer.id,
      removeLegendId: legendId
    });
  }
  setUiState({ showLegendSelector: null });
}

function makeChoice(value) {
  if (gameState.isHost) {
    setGameState((prev) => {
      const s = { ...prev, activeChoice: null };
      window._lastChoice = value;
      broadcastState(s);
      return s;
    });
  } else {
    connections[0]?.send({ type: 'MAKE_CHOICE', value });
  }
}

function renderIcon(icon, className, fallback) {
  if (!icon) return '';
  const lower = icon.toLowerCase();
  const isImage = lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.webp') || lower.startsWith('data:');
  if (isImage) {
    return `<img src="${escapeHtml(icon)}" alt="Icon" class="pointer-events-none select-none ${escapeHtml(className || '')}" data-fallback="${escapeHtml(fallback || '💀')}" />`;
  }
  return `<span class="${escapeHtml(className || '')}">${escapeHtml(icon)}</span>`;
}

function renderDice(value, rolling) {
  return `<div class="w-16 h-16 bg-white border-4 border-slate-700 rounded-2xl flex items-center justify-center text-3xl font-bold text-slate-900 shadow-[0_10px_0_0_#cbd5e1] active:translate-y-1 active:shadow-none transition-all ${rolling ? 'animate-bounce' : ''}">${rolling ? '🎲' : (value || '?')}</div>`;
}

function getItemIcon(item) {
  if (item === 'Pati Picante') return '???';
  if (item === 'Casado') return '🍱';
  if (item === 'Chifrijo') return '🍲';
  if (item === 'Campesino') return '👨‍🌾';
  if (item === 'Rice and Beans') return '🍚';
  if (item === 'Leche Dormida') return '🥛';
  if (item === 'Sopa de Mondongo') return '🍜';
  if (item === 'Suspiros Costarricenses') return '🍬';
  if (item === 'Trago de Cacique') return '🍺';
  if (item === 'Sanatorio') return '/duran.webp';
  if (item === 'Gallo Pinto') return '🍛';
  if (item === 'Algodón de Azúcar') return '🍭';
  if (item === 'Caldosa') return '🥣';
  if (item === 'Cajetas de Leche') return '🍬';
  if (item === 'Cafe') return '☕';
  return '🥃';
}
function renderHelpModal() {
  const legends = Object.values(LEGENDS_DATA)
    .map(
      (legend) => `
        <div class="bg-slate-800/40 p-4 rounded-2xl border border-slate-700/50">
          <div class="flex items-center gap-3 mb-2">
            ${renderIcon(legend.icon, 'w-8 h-8 text-2xl flex items-center justify-center', '💀')}
            <span class="font-bold text-orange-200">${escapeHtml(legend.name)}</span>
          </div>
          <p class="text-slate-400 text-xs leading-relaxed">${escapeHtml(legend.effect)}</p>
        </div>
      `
    )
    .join('');

  return `
    <div class="fixed inset-0 bg-black/95 backdrop-blur-xl z-[500] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div class="bg-slate-900 border-2 border-orange-500/50 w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-[2rem] p-6 md:p-10 shadow-[0_0_100px_rgba(249,115,22,0.2)] no-scrollbar">
        <div class="flex justify-between items-start mb-8">
          <div>
            <h2 class="text-4xl font-spooky text-orange-500 mb-1">Manual de Supervivencia</h2>
            <p class="text-slate-500 text-xs uppercase tracking-[0.2em] font-bold">Guía oficial de la ruta</p>
          </div>
          <button onclick="app.closeHelp()" class="bg-slate-800 hover:bg-slate-700 p-3 rounded-full text-slate-400 transition-colors">
            <span class="text-2xl leading-none">&times;</span>
          </button>
        </div>

        <div class="space-y-10">
          <section>
            <h3 class="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span class="text-orange-500">💀</span> Leyendas y Peligros
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              ${legends}
            </div>
          </section>

          <section>
            <h3 class="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span class="text-yellow-500">🍚</span> Objetos y Comida
            </h3>
            <div class="space-y-3">
              <div class="bg-yellow-900/10 p-4 rounded-2xl border border-yellow-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🍱</span>
                  <span class="font-bold text-yellow-500 uppercase text-sm">Casado Completo</span>
                </div>
                <p class="text-slate-400 text-xs">Mueve a un rival a CUALQUIER casilla del tablero.</p>
              </div>
              <div class="bg-green-900/10 p-4 rounded-2xl border border-green-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🍚</span>
                  <span class="font-bold text-green-500 uppercase text-sm">Rice and Beans</span>
                </div>
                <p class="text-slate-400 text-xs">Intercambia esta carta por CUALQUIER objeto o comida del inventario de un rival. ¡El robo perfecto!</p>
              </div>
              <div class="bg-red-900/10 p-4 rounded-2xl border border-red-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🍲</span>
                  <span class="font-bold text-red-500 uppercase text-sm">Chifrijo</span>
                </div>
                <p class="text-slate-400 text-xs">Le robas 2 tamales a un rival.</p>
              </div>
              <div class="bg-purple-900/10 p-4 rounded-2xl border border-purple-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🥃</span>
                  <span class="font-bold text-purple-500 uppercase text-sm">Trago de Cacique</span>
                </div>
                <p class="text-slate-400 text-xs">Hace que un rival pierda su próximo turno.</p>
              </div>
              <div class="bg-emerald-900/10 p-4 rounded-2xl border border-emerald-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">👨‍🌾</span>
                  <span class="font-bold text-emerald-400 uppercase text-sm">Campesino</span>
                </div>
                <p class="text-slate-400 text-xs">Te protege de maldiciones de leyendas, excepto La Mona.</p>
              </div>
              <div class="bg-amber-900/10 p-4 rounded-2xl border border-amber-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">☕</span>
                  <span class="font-bold text-amber-400 uppercase text-sm">Café</span>
                </div>
                <p class="text-slate-400 text-xs">Se usa una vez para retroceder 1 casilla y evitar una maldición.</p>
              </div>
              <div class="bg-pink-900/10 p-4 rounded-2xl border border-pink-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🍬</span>
                  <span class="font-bold text-pink-400 uppercase text-sm">Cajetas de Leche</span>
                </div>
                <p class="text-slate-400 text-xs">Endulzan a los duendes y se consumen automáticamente.</p>
              </div>
              
              <div class="bg-emerald-900/10 p-4 rounded-2xl border border-emerald-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">&#x1F963;</span>
                  <span class="font-bold text-emerald-400 uppercase text-sm">Caldosa</span>
                </div>
                <p class="text-slate-400 text-xs">Robas 2 tamales a un rival y compartes 1 con otro jugador.</p>
              </div>
              <div class="bg-sky-900/10 p-4 rounded-2xl border border-sky-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">&#x1F95B;</span>
                  <span class="font-bold text-sky-400 uppercase text-sm">Leche Dormida</span>
                </div>
                <p class="text-slate-400 text-xs">Se la das a un rival. Solo podra avanzar 2 casillas por turno.</p>
              </div>
              <div class="bg-lime-900/10 p-4 rounded-2xl border border-lime-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">&#x1F35C;</span>
                  <span class="font-bold text-lime-400 uppercase text-sm">Sopa de Mondongo</span>
                </div>
                <p class="text-slate-400 text-xs">Se la lanzas a un rival. Te da 1 tamal y retrocede 2 casillas.</p>
              </div>
              <div class="bg-fuchsia-900/10 p-4 rounded-2xl border border-fuchsia-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🍬</span>
                  <span class="font-bold text-fuchsia-400 uppercase text-sm">Suspiros Costarricenses</span>
                </div>
                <p class="text-slate-400 text-xs">Revive a un jugador muerto con 2 tamales (o te salva al morir).</p>
              </div>
              <div class="bg-orange-900/10 p-4 rounded-2xl border border-orange-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">&#x1F336;&#xFE0F;</span>
                  <span class="font-bold text-orange-400 uppercase text-sm">Pati Picante</span>
                </div>
                <p class="text-slate-400 text-xs">Haces retroceder 2 casillas a un rival. Si tiene arroz con leche, te da 1.</p>
              </div>
              <div class="bg-amber-900/10 p-4 rounded-2xl border border-amber-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🍛</span>
                  <span class="font-bold text-amber-500 uppercase text-sm">Gallo Pinto</span>
                </div>
                <p class="text-slate-400 text-xs">Elimina una leyenda del tablero.</p>
              </div>
              <div class="bg-pink-900/10 p-4 rounded-2xl border border-pink-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🍭</span>
                  <span class="font-bold text-pink-400 uppercase text-sm">Algodón de Azúcar</span>
                </div>
                <p class="text-slate-400 text-xs">Conviértelo en 2 arroz con leche o úsalo para evitar la maldición de las hormigas.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 class="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span class="text-cyan-400">🎲</span> Casillas Especiales
            </h3>
            <div class="space-y-3">
              <div class="bg-cyan-900/10 p-4 rounded-2xl border border-cyan-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🎁</span>
                  <span class="font-bold text-cyan-400 uppercase text-sm">Caja Sorpresa</span>
                </div>
                <p class="text-slate-400 text-xs">Te puede dar tamales o una carta especial. El Gallo Pinto aparece solo una vez por partida.</p>
              </div>
              <div class="bg-indigo-900/10 p-4 rounded-2xl border border-indigo-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🎲</span>
                  <span class="font-bold text-indigo-400 uppercase text-sm">Turno (Minijuego)</span>
                </div>
                <p class="text-slate-400 text-xs">Lanzas un dado especial y puedes ganar tamales o un objeto.</p>
              </div>
              <div class="bg-emerald-900/10 p-4 rounded-2xl border border-emerald-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🥕</span>
                  <span class="font-bold text-emerald-400 uppercase text-sm">Feria del Agricultor</span>
                </div>
                <p class="text-slate-400 text-xs">Recibes comida especial: cafe, cajetas, caldosa, mondongo o suspiros.</p>
              </div>
              <div class="bg-rose-900/10 p-4 rounded-2xl border border-rose-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">&#x1F91D;</span>
                  <span class="font-bold text-rose-400 uppercase text-sm">Pacto del Jocote</span>
                </div>
                <p class="text-slate-400 text-xs">Si dos jugadores se juntan, quedan aliados por 2 turnos. Si uno pierde tamales por maldicion, el otro pierde la mitad.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 class="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span class="text-amber-400">🍮</span> Provincias
            </h3>
            <div class="space-y-3">
              <div class="bg-amber-900/10 p-4 rounded-2xl border border-amber-500/20">
                <div class="flex items-center gap-3 mb-1">
                  <span class="text-2xl">🍮</span>
                  <span class="font-bold text-amber-500 uppercase text-sm">Arroz con Leche</span>
                </div>
                <p class="text-slate-400 text-xs">Cada provincia te da 1. Al final, cada 2 te dan 1 tamal extra. Con 3 puedes evitar una maldición de leyenda.</p>
              </div>
            </div>
          </section>
        </div>

        <button onclick="app.closeHelp()" class="w-full mt-8 py-4 bg-orange-600 hover:bg-orange-500 text-white font-black rounded-2xl transition-all">
          ¡LISTO PARA JUGAR!
        </button>
      </div>
    </div>
  `;
}

function renderInventoryModal(inventory, canUse) {
  const itemsHtml = inventory.length
    ? inventory
        .map(
          (item) => `
            <div class="flex items-center justify-between p-4 rounded-2xl border bg-slate-800/40 border-slate-700/50 transition-all">
              <div class="flex items-center gap-4">
                <span class="text-3xl">${getItemIcon(item)}</span>
                <div>
                  <h4 class="font-bold text-white uppercase text-sm tracking-tight">${escapeHtml(item)}</h4>
                </div>
              </div>
              <button
                onclick="app.useInventoryItem('${escapeHtml(item)}')"
                ${canUse ? '' : 'disabled'}
                class="px-6 py-2 rounded-xl text-[10px] font-black transition-all ${
                  canUse
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg active:scale-95'
                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                }"
              >
                USAR
              </button>
            </div>
          `
        )
        .join('')
    : `
        <div class="flex flex-col items-center justify-center py-20 opacity-30">
          <span class="text-6xl mb-4">🎒</span>
          <p class="text-white font-bold italic">No tienes nada todavía...</p>
        </div>
      `;

  return `
    <div class="fixed inset-0 bg-black/95 backdrop-blur-xl z-[450] flex items-center justify-center p-4 animate-in zoom-in duration-300">
      <div class="bg-slate-900 border-2 border-indigo-500/50 w-full max-w-xl rounded-[2.5rem] p-8 shadow-2xl overflow-hidden flex flex-col">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-3xl font-spooky text-indigo-400">Tu Mochila</h2>
            <p class="text-slate-500 text-[10px] uppercase font-bold tracking-widest">Objetos recolectados</p>
          </div>
          <button onclick="app.closeInventory()" class="text-slate-400 hover:text-white text-3xl">&times;</button>
        </div>

        <div class="flex-1 overflow-y-auto pr-2 no-scrollbar space-y-3 min-h-[300px]">
          ${itemsHtml}
        </div>

        <button onclick="app.closeInventory()" class="w-full mt-6 py-3 border-2 border-slate-700 text-slate-400 rounded-xl font-bold hover:bg-slate-800 transition-all">
          CERRAR MOCHILA
        </button>
      </div>
    </div>
  `;
}

function renderEventPopup(event, isMyControl) {
  return `
    <div class="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-[550] p-6 animate-in fade-in duration-300">
      <div class="w-full max-sm p-8 rounded-[3rem] border-4 shadow-2xl text-center transform animate-in zoom-in duration-500 ${
        event.type === 'BAD' ? 'border-red-600 bg-slate-900' : 'border-green-600 bg-slate-900'
      }">
        <div class="flex items-center justify-center mb-6">
          ${renderIcon(event.icon, 'text-7xl block animate-bounce w-32 h-32 object-contain', '💀')}
        </div>
        <h2 class="text-4xl font-spooky mb-4 ${event.type === 'BAD' ? 'text-red-500' : 'text-green-500'}">${escapeHtml(event.title)}</h2>
        <p class="text-white text-lg mb-8 leading-tight font-medium">${escapeHtml(event.message)}</p>

        ${
          isMyControl
            ? `<button onclick="app.closeEvent()" class="w-full py-4 bg-white text-slate-900 rounded-2xl font-black text-xl hover:bg-slate-200 transition-all shadow-xl active:scale-95">¡ENTENDIDO!</button>`
            : `<div class="text-slate-500 italic text-sm animate-pulse">Esperando...</div>`
        }
      </div>
    </div>
  `;
}

function renderUiAlert(alert) {
  const borderClass = alert.type === 'BAD'
    ? 'border-red-600 text-red-400'
    : alert.type === 'GOOD'
      ? 'border-green-600 text-green-400'
      : 'border-slate-600 text-slate-200';
  return `
    <div class="fixed inset-0 bg-black/90 backdrop-blur-xl flex items-center justify-center z-[700] p-6 animate-in fade-in duration-300">
      <div class="w-full max-w-md p-8 rounded-[2.5rem] border-2 bg-slate-900 shadow-2xl text-center ${borderClass}">
        <h2 class="text-2xl font-spooky mb-4">${escapeHtml(alert.title || 'Aviso')}</h2>
        <p class="text-white text-base mb-8 leading-tight font-medium">${escapeHtml(alert.message || '')}</p>
        <button onclick="app.closeUiAlert()" class="w-full py-3 bg-white text-slate-900 rounded-2xl font-black text-lg hover:bg-slate-200 transition-all shadow-xl active:scale-95">ENTENDIDO</button>
      </div>
    </div>
  `;
}

function renderChoiceModal(choice, isMyTurnControl, playerTamales) {
  const choicePlayer = choice.targetId != null
    ? gameState.players.find((p) => p.id === choice.targetId)
    : (Number.isInteger(choice.playerIdx) ? gameState.players[choice.playerIdx] : null);
  const choicePlayerName = choicePlayer?.name || '';
  if (choice.type === 'RIO_ORO') {
    const voters = choice.voters || [];
    const answers = choice.answers || {};
    const options = choice.options || [];
    const pending = voters.filter((id) => !answers[id]);
    const myPlayer = getMyPlayer();
    const canAnswerOnline = !!gameState.roomCode && myPlayer && voters.includes(myPlayer.id) && !answers[myPlayer.id];
    const alreadyAnsweredOnline = !!gameState.roomCode && myPlayer && answers[myPlayer.id];

    const localAnswerControls = pending.length
      ? pending
          .map((voterId) => {
            const voter = gameState.players.find((p) => p.id === voterId);
            const optionButtons = options
              .map((opt) => `<button onclick="app.castRioOroAnswer('${escapeHtml(opt)}', ${voterId})" class="px-3 py-2 rounded-lg bg-emerald-600 text-white text-[10px] font-black">${escapeHtml(opt)}</button>`)
              .join('');
            return `
              <div class="bg-slate-800/70 border border-slate-700 rounded-xl p-3 space-y-3">
                <span class="text-xs font-bold text-slate-200">${escapeHtml(voter?.name || 'Jugador')}</span>
                <div class="grid grid-cols-1 gap-2">${optionButtons}</div>
              </div>
            `;
          })
          .join('')
      : `<div class="text-slate-500 italic text-sm">Todos ya respondieron.</div>`;

    const onlineControls = canAnswerOnline
      ? `
        <div class="grid grid-cols-1 gap-3">
          ${options
            .map((opt) => `<button onclick="app.castRioOroAnswer('${escapeHtml(opt)}')" class="w-full py-3 rounded-2xl font-black text-xs bg-emerald-600 hover:bg-emerald-500 text-white shadow-xl active:scale-95">${escapeHtml(opt)}</button>`)
            .join('')}
        </div>
      `
      : `<div class="text-slate-500 italic text-sm">${alreadyAnsweredOnline ? 'Respuesta enviada.' : 'Esperando respuestas...'}</div>`;

    return `
      <div class="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-[550] p-6 animate-in fade-in duration-300">
        <div class="w-full max-w-md bg-slate-900 border-4 border-emerald-500 p-8 rounded-[3rem] shadow-2xl text-center">
          ${choicePlayerName ? `<div class="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2">Reto de ${escapeHtml(choicePlayerName)}</div>` : ''}
          <h2 class="text-3xl font-spooky text-emerald-400 mb-4">${escapeHtml(choice.title)}</h2>
          <p class="text-white text-base mb-6 leading-tight">${escapeHtml(choice.message)}</p>
          ${gameState.roomCode ? onlineControls : `<div class="space-y-3">${localAnswerControls}</div>`}
          <div class="mt-6 text-xs text-slate-500">${pending.length} respuesta(s) pendiente(s)</div>
        </div>
      </div>
    `;
  }
  if (choice.type === 'SANLUCAS_VOTE') {
    const voters = choice.voters || [];
    const votes = choice.votes || {};
    const candidates = choice.candidates || [];
    const pending = voters.filter((id) => !votes[id]);
    const myPlayer = getMyPlayer();
    const canVoteOnline = !!gameState.roomCode && myPlayer && voters.includes(myPlayer.id) && !votes[myPlayer.id];
    const alreadyVotedOnline = !!gameState.roomCode && myPlayer && votes[myPlayer.id];

    const counts = candidates.reduce((acc, id) => {
      acc[id] = 0;
      return acc;
    }, {});
    Object.values(votes).forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
    const tally = candidates
      .map((id) => {
        const player = gameState.players.find((p) => p.id === id);
        return `<span class="bg-slate-800 border border-slate-700 px-3 py-1 rounded-full text-[10px]">${escapeHtml(player?.name || 'Jugador')}: ${counts[id] || 0}</span>`;
      })
      .join('');

    const localVoteControls = pending.length
      ? pending
          .map((voterId) => {
            const voter = gameState.players.find((p) => p.id === voterId);
            const candidateButtons = candidates
              .map((id) => {
                const candidate = gameState.players.find((p) => p.id === id);
                return `<button onclick="app.castSanLucasVote(${id}, ${voterId})" class="px-3 py-2 rounded-lg bg-indigo-600 text-white text-[10px] font-black">${escapeHtml(candidate?.name || 'Jugador')}</button>`;
              })
              .join('');
            return `
              <div class="bg-slate-800/70 border border-slate-700 rounded-xl p-3 space-y-3">
                <span class="text-xs font-bold text-slate-200">${escapeHtml(voter?.name || 'Jugador')}</span>
                <div class="grid grid-cols-2 gap-2">${candidateButtons}</div>
              </div>
            `;
          })
          .join('')
      : `<div class="text-slate-500 italic text-sm">Todos ya votaron.</div>`;

    const onlineControls = canVoteOnline
      ? `
        <div class="grid grid-cols-2 gap-3">
          ${candidates
            .map((id) => {
              const candidate = gameState.players.find((p) => p.id === id);
              return `<button onclick="app.castSanLucasVote(${id})" class="w-full py-3 rounded-2xl font-black text-xs bg-indigo-600 hover:bg-indigo-500 text-white shadow-xl active:scale-95">${escapeHtml(candidate?.name || 'Jugador')}</button>`;
            })
            .join('')}
        </div>
      `
      : `<div class="text-slate-500 italic text-sm">${alreadyVotedOnline ? 'Voto enviado.' : 'Esperando votos...'}</div>`;

    return `
      <div class="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-[550] p-6 animate-in fade-in duration-300">
        <div class="w-full max-w-md bg-slate-900 border-4 border-indigo-500 p-8 rounded-[3rem] shadow-2xl text-center">
          ${choicePlayerName ? `<div class="text-[10px] uppercase tracking-[0.3em] text-indigo-300 mb-2">Reto de ${escapeHtml(choicePlayerName)}</div>` : ''}
          <h2 class="text-3xl font-spooky text-indigo-400 mb-4">${escapeHtml(choice.title)}</h2>
          <p class="text-white text-base mb-6 leading-tight">${escapeHtml(choice.message)}</p>
          <div class="flex flex-wrap items-center justify-center gap-2 text-xs font-bold text-slate-300 mb-6">
            ${tally}
          </div>
          ${gameState.roomCode ? onlineControls : `<div class="space-y-3">${localVoteControls}</div>`}
        </div>
      </div>
    `;
  }
  if (choice.type === 'MONA_VOTE') {
    const voters = choice.voters || [];
    const votes = choice.votes || {};
    const approveCount = Object.values(votes).filter((v) => v === 'APPROVE').length;
    const rejectCount = Object.values(votes).filter((v) => v === 'REJECT').length;
    const pending = voters.filter((id) => !votes[id]);
    const myPlayer = getMyPlayer();
    const canVoteOnline = !!gameState.roomCode && myPlayer && voters.includes(myPlayer.id) && !votes[myPlayer.id];
    const alreadyVotedOnline = !!gameState.roomCode && myPlayer && votes[myPlayer.id];
    const isTarget = myPlayer && choice.targetId === myPlayer.id;

    const localVoteControls = pending.length
      ? pending
          .map((voterId) => {
            const voter = gameState.players.find((p) => p.id === voterId);
            return `
              <div class="flex items-center justify-between bg-slate-800/70 border border-slate-700 rounded-xl p-3">
                <span class="text-sm font-bold text-slate-100">${escapeHtml(voter?.name || 'Jugador')}</span>
                <div class="flex gap-2">
                  <button onclick="app.castMonaVote('APPROVE', ${voterId})" class="px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-black">Aprobar</button>
                  <button onclick="app.castMonaVote('REJECT', ${voterId})" class="px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-black">Rechazar</button>
                </div>
              </div>
            `;
          })
          .join('')
      : `<div class="text-slate-500 italic text-sm">Todos ya votaron.</div>`;

    const onlineControls = canVoteOnline
      ? `
        <div class="flex gap-3 justify-center">
          <button onclick="app.castMonaVote('APPROVE')" class="w-full py-4 rounded-2xl font-black text-sm bg-green-600 hover:bg-green-500 text-white shadow-xl active:scale-95">Aprobar</button>
          <button onclick="app.castMonaVote('REJECT')" class="w-full py-4 rounded-2xl font-black text-sm bg-red-600 hover:bg-red-500 text-white shadow-xl active:scale-95">Rechazar</button>
        </div>
      `
      : `<div class="text-slate-500 italic text-sm">${isTarget ? 'No puedes votar en tu propio reto.' : (alreadyVotedOnline ? 'Voto enviado.' : 'Esperando votos...')}</div>`;

    return `
      <div class="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-[550] p-6 animate-in fade-in duration-300">
        <div class="w-full max-w-md bg-slate-900 border-4 border-orange-500 p-8 rounded-[3rem] shadow-2xl text-center">
          ${choicePlayerName ? `<div class="text-[10px] uppercase tracking-[0.3em] text-orange-300 mb-2">Reto de ${escapeHtml(choicePlayerName)}</div>` : ''}
          <h2 class="text-3xl font-spooky text-orange-400 mb-4">${escapeHtml(choice.title)}</h2>
          <p class="text-white text-base mb-6 leading-tight">${escapeHtml(choice.message)}</p>
          <div class="flex items-center justify-center gap-4 text-xs font-bold text-slate-300 mb-6">
            <span class="bg-green-900/40 border border-green-500/30 px-3 py-1 rounded-full">Aprobados: ${approveCount}</span>
            <span class="bg-red-900/40 border border-red-500/30 px-3 py-1 rounded-full">Rechazados: ${rejectCount}</span>
            <span class="bg-slate-800 border border-slate-600 px-3 py-1 rounded-full">Pendientes: ${pending.length}</span>
          </div>
          ${gameState.roomCode ? onlineControls : `<div class="space-y-3">${localVoteControls}</div>`}
        </div>
      </div>
    `;
  }

  const optionsHtml = choice.options
    .map((opt) => {
      const canAfford = !opt.cost || playerTamales >= opt.cost;
      return `
        <button
          ${canAfford ? `onclick="app.makeChoice('${escapeHtml(opt.value)}')"` : 'disabled'}
          class="w-full py-4 rounded-2xl font-black text-sm flex items-center justify-center gap-3 transition-all ${
            canAfford
              ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-xl active:scale-95'
              : 'bg-slate-800 text-slate-600 opacity-50 cursor-not-allowed'
          }"
        >
          ${renderIcon(opt.icon, 'w-5 h-5 text-base', '❓')}
          <span class="uppercase tracking-tight">${escapeHtml(opt.label)}</span>
          ${opt.cost ? `<span class="text-xs bg-black/20 px-2 py-1 rounded-lg">-${opt.cost} 🫔</span>` : ''}
        </button>
      `;
    })
    .join('');

  return `
    <div class="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-[550] p-6 animate-in fade-in duration-300">
      <div class="w-full max-w-md bg-slate-900 border-4 border-indigo-600 p-8 rounded-[3rem] shadow-2xl text-center">
        ${choicePlayerName ? `<div class="text-[10px] uppercase tracking-[0.3em] text-indigo-300 mb-2">Reto de ${escapeHtml(choicePlayerName)}</div>` : ''}
        <h2 class="text-3xl font-spooky text-indigo-400 mb-4">${escapeHtml(choice.title)}</h2>
        <p class="text-white text-lg mb-8 leading-tight">${escapeHtml(choice.message)}</p>

        ${isMyTurnControl ? `<div class="space-y-4">${optionsHtml}</div>` : `<div class="text-slate-500 italic text-sm animate-pulse">Esperando decisión del jugador...</div>`}
      </div>
    </div>
  `;
}

function renderLegendModal(legend) {
  const iconClass = legend.id === MONA_LEGEND_ID ? 'w-36 h-36 object-contain' : 'text-8xl';
  return `
    <div class="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[600] p-4">
      <div class="bg-slate-900 border-2 border-orange-500/50 p-8 rounded-[3rem] max-w-lg w-full shadow-2xl relative animate-in zoom-in duration-300">
        <button onclick="app.closeLegend()" class="absolute top-6 right-6 text-slate-400 hover:text-white text-2xl z-10">&times;</button>
        <div class="text-center mb-6 relative">
          <div class="mx-auto w-48 h-48 mb-6 flex items-center justify-center rounded-2xl overflow-hidden bg-slate-800/50">
            ${renderIcon(legend.icon, iconClass, '💀')}
          </div>
          <h2 class="text-4xl font-spooky text-orange-500 tracking-tight">${escapeHtml(legend.name)}</h2>
        </div>
        <p class="text-sm leading-relaxed mb-6 font-medium text-slate-200">${escapeHtml(legend.history)}</p>
        <div class="p-4 rounded-2xl bg-orange-500/10 border border-orange-500/20 text-orange-200 text-sm font-bold">
          <span class="uppercase text-[10px] tracking-widest block mb-1 opacity-60">Efecto</span>
          ${escapeHtml(legend.effect)}
        </div>
      </div>
    </div>
  `;
}

function renderTargetSelector() {
  const myPlayer = getMyPlayer();
  if (!uiState.showTargetSelector || !myPlayer) return '';

  let title = 'Elegir Rival';
  if (uiState.showTargetSelector.itemId === 'Duendes') title = 'Duelo de Duendes';
  else if (uiState.showTargetSelector.itemId === 'Llorona') title = 'Dar Tamales';
  else if (uiState.showTargetSelector.itemId === 'Chifrijo') title = 'Robar Tamales';
  else if (uiState.showTargetSelector.itemId === 'Casado') title = 'Mover de Casilla';
  else if (uiState.showTargetSelector.itemId === 'Rice and Beans') title = 'Intercambiar Objeto';
  else if (uiState.showTargetSelector.itemId === 'Sanatorio') title = 'Desorientar Rival';
  else if (uiState.showTargetSelector.itemId === 'Caldosa') title = 'Robar 2 Tamales';
  else if (uiState.showTargetSelector.itemId === 'Pati Picante') title = 'Pati Picante';
  else if (uiState.showTargetSelector.itemId === 'Sopa de Mondongo') title = 'Sopa de Mondongo';
  else if (uiState.showTargetSelector.itemId === 'Suspiros Costarricenses') title = 'Suspiros Costarricenses';
  else if (uiState.showTargetSelector.itemId === 'Leche Dormida') title = 'Leche Dormida';

  const players = gameState.players
    .filter((p) => {
      if (uiState.showTargetSelector.itemId === 'Suspiros Costarricenses') {
        return p.id !== myPlayer.id && p.tamales <= 0;
      }
      if (p.id === myPlayer.id || p.isFinished || p.tamales <= 0) return false;
      if (uiState.showTargetSelector.itemId === 'Caldosa') {
        return getCaldosaCandidates([myPlayer.id, p.id]).length > 0;
      }
      return true;
    })
    .map((p) => `<button onclick="app.useItemInitiator(${p.id})" class="w-full p-4 rounded-2xl bg-slate-800 border border-slate-700 hover:border-purple-500 text-white font-bold">${escapeHtml(p.name)}</button>`)
    .join('');

  return `
    <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-[600] flex items-center justify-center p-6">
      <div class="bg-slate-900 p-8 rounded-[2.5rem] border-2 border-slate-700 w-full max-sm text-center">
        <h3 class="text-2xl font-spooky text-purple-400 mb-6">${escapeHtml(title)}</h3>
        <p class="text-slate-400 mb-6 text-sm">¿A quién le harás esto?</p>
        <div class="space-y-3 mb-6">
          ${players}
        </div>
        <button onclick="app.clearTargetSelector()" class="text-slate-500 font-black">CANCELAR</button>
      </div>
    </div>
  `;
}

function renderCasadoSelector() {
  return `
    <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-[600] flex items-center justify-center p-6">
      <div class="bg-slate-900 p-8 rounded-[2.5rem] border-2 border-slate-700 w-full max-sm text-center">
        <h3 class="text-2xl font-spooky text-yellow-400 mb-6">Mover Rival</h3>
        <p class="text-slate-400 mb-4 text-sm">¿A qué casilla lo envías?</p>
        <div class="grid grid-cols-5 gap-2 h-40 overflow-y-auto p-2 no-scrollbar">
          ${getBoard().map((t, idx) => `<button onclick="app.selectCasadoDestination(${idx})" class="bg-slate-800 p-2 rounded-lg text-xs font-bold border border-slate-700 hover:bg-yellow-600">${idx + 1}</button>`).join('')}
        </div>
        <button onclick="app.clearCasadoTarget()" class="mt-6 text-slate-500 font-black">ATRÁS</button>
      </div>
    </div>
  `;
}

function renderCaldosaShareSelector() {
  const myPlayer = getMyPlayer();
  if (!myPlayer || !uiState.caldosaShareTargetId || uiState.caldosaStealTargetId == null) return '';

  const players = gameState.players
    .filter((p) => p.id !== myPlayer.id && p.id !== uiState.caldosaStealTargetId && !p.isFinished && p.tamales > 0)
    .map((p) => `<button onclick="app.selectCaldosaShareTarget(${p.id})" class="w-full p-4 rounded-2xl bg-slate-800 border border-slate-700 hover:border-amber-500 text-white font-bold">${escapeHtml(p.name)}</button>`)
    .join('');

  return `
    <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-[600] flex items-center justify-center p-6">
      <div class="bg-slate-900 p-8 rounded-[2.5rem] border-2 border-slate-700 w-full max-sm text-center">
        <h3 class="text-2xl font-spooky text-amber-400 mb-6">Compartir Caldosa</h3>
        <p class="text-slate-400 mb-6 text-sm">Elige a quien regalar 1 tamal.</p>
        <div class="space-y-3 mb-6">
          ${players}
        </div>
        <button onclick="app.clearCaldosaTargets()" class="text-slate-500 font-black">CANCELAR</button>
      </div>
    </div>
  `;
}

function renderStealSelector() {
  const target = gameState.players.find((p) => p.id === uiState.stealTargetId);
  const items = target?.inventory || [];
  const list = items.length
    ? items
        .map(
          (item) => `
            <button onclick="app.selectStolenItem('${escapeHtml(item)}')" class="w-full p-4 rounded-xl bg-slate-800 border border-slate-700 text-white font-bold hover:bg-green-600/20 hover:border-green-500">
              ${escapeHtml(item)}
            </button>
          `
        )
        .join('')
    : '<p class="text-white italic py-4">¡No tiene objetos ni comida!</p>';

  return `
    <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-[600] flex items-center justify-center p-6">
      <div class="bg-slate-900 p-8 rounded-[2.5rem] border-2 border-slate-700 w-full max-sm text-center">
        <h3 class="text-2xl font-spooky text-green-400 mb-6">Intercambio</h3>
        <p class="text-slate-400 mb-4 text-sm">Elige un objeto o comida de ${escapeHtml(target?.name || '')}</p>
        <div class="space-y-3 max-h-60 overflow-y-auto pr-2 no-scrollbar">
          ${list}
        </div>
        <button onclick="app.clearStealTarget()" class="mt-6 text-slate-500 font-black">ATRÁS</button>
      </div>
    </div>
  `;
}

function renderLegendSelector() {
  const myPlayer = getMyPlayer();
  if (!uiState.showLegendSelector || !myPlayer) return '';

  const legends = [];
  const seen = new Set();
  getBoard().forEach((tile) => {
    if (tile.legendId && !seen.has(tile.legendId)) {
      seen.add(tile.legendId);
      legends.push(tile.legendId);
    }
  });

  const list = legends.length
    ? legends
        .map((id) => {
          const legend = LEGENDS_DATA[id];
          return `
            <button onclick="app.selectLegendToRemove('${escapeHtml(id)}')" class="w-full p-4 rounded-xl bg-slate-800 border border-slate-700 text-white font-bold hover:bg-amber-600/20 hover:border-amber-500">
              ${renderIcon(legend?.icon || '🧙‍♀️', 'w-5 h-5 text-base', '🧙‍♀️')}
              <span class="ml-2">${escapeHtml(legend?.name || id)}</span>
            </button>
          `;
        })
        .join('')
    : '<p class="text-white italic py-4">No hay leyendas para eliminar.</p>';

  return `
    <div class="fixed inset-0 bg-black/80 backdrop-blur-sm z-[600] flex items-center justify-center p-6">
      <div class="bg-slate-900 p-8 rounded-[2.5rem] border-2 border-slate-700 w-full max-sm text-center">
        <h3 class="text-2xl font-spooky text-amber-400 mb-6">Eliminar Leyenda</h3>
        <p class="text-slate-400 mb-4 text-sm">Elige una leyenda para remover del tablero.</p>
        <div class="space-y-3 max-h-60 overflow-y-auto pr-2 no-scrollbar">
          ${list}
        </div>
        <button onclick="app.clearLegendSelector()" class="mt-6 text-slate-500 font-black">CANCELAR</button>
      </div>
    </div>
  `;
}

function renderWinnerModal() {
  const winner = gameState.winner;
  if (!winner) return '';
  const sortedPlayers = [...gameState.players].sort((a, b) => b.tamales - a.tamales);

  const rows = sortedPlayers
    .map(
      (p, i) => `
        <div class="flex justify-between items-center p-4 bg-slate-800/30 rounded-2xl border border-slate-700">
          <div class="flex items-center gap-4">
            <span class="text-xl font-black text-slate-500">${i + 1}°</span>
            <div class="w-4 h-4 rounded-full" style="background-color: ${p.color}"></div>
            <span class="font-bold ${p.tamales <= 0 ? 'text-slate-600' : 'text-white'}">${escapeHtml(p.name)} ${p.tamales <= 0 ? '(💀)' : ''}</span>
          </div>
          <span class="font-black text-yellow-500">🫔 ${p.tamales}</span>
        </div>
      `
    )
    .join('');

  return `
    <div class="fixed inset-0 bg-black/98 z-[300] flex items-center justify-center p-6 text-center text-white backdrop-blur-3xl overflow-y-auto">
      <div class="bg-slate-900 border-4 border-orange-500 p-8 md:p-12 rounded-[4rem] animate-in zoom-in duration-500 w-full max-w-2xl">
        <h2 class="text-5xl md:text-7xl font-spooky text-orange-500 mb-6 animate-bounce">RUTA FINALIZADA</h2>

        <div class="mb-8 p-6 bg-slate-800/50 rounded-3xl border-2 border-slate-700">
          <h3 class="text-2xl font-black text-yellow-500 uppercase mb-4 tracking-tighter">EL REY DEL TAMAL</h3>
          <div class="flex flex-col items-center">
            <div class="w-24 h-24 rounded-full mb-4 border-4 border-white shadow-[0_0_30px_white]" style="background-color: ${winner.color}"></div>
            <p class="text-4xl font-black uppercase tracking-tight">${escapeHtml(winner.name)}</p>
            <p class="text-2xl text-yellow-500 font-bold">🫔 ${winner.tamales} Tamales</p>
          </div>
        </div>

        <div class="space-y-3 mb-10">
          <h4 class="text-xs font-bold text-slate-500 uppercase tracking-widest text-left px-2">Posiciones Finales</h4>
          ${rows}
        </div>

        <button onclick="app.initializeMatch()" class="w-full bg-green-600 p-5 rounded-2xl font-black text-xl hover:bg-green-500 transition-all shadow-lg">Jugar de Nuevo</button>
        <button onclick="app.resetToMenu()" class="w-full mt-4 bg-slate-800 p-4 rounded-2xl font-bold border border-slate-700">Ir al Menú</button>
      </div>
    </div>
  `;
}

function renderTurnoModal() {
  return `
    <div class="fixed inset-0 bg-black/98 z-[300] flex items-center justify-center p-6 backdrop-blur-3xl">
      <div class="bg-slate-900 border-8 border-indigo-600 p-10 rounded-[4rem] text-center max-w-sm">
        <h2 class="text-4xl font-spooky text-indigo-400 mb-6 italic">¡TURNO LOCAL!</h2>
        ${renderDice(uiState.turnoRoll, uiState.isTurnoRolling)}
        ${(gameState.isHost || !gameState.roomCode) ? `<button onclick="app.rollTurno()" class="w-full mt-8 py-6 bg-indigo-600 text-white rounded-3xl font-black text-2xl">LANZAR</button>` : ''}
      </div>
    </div>
  `;
}

function renderDuenoModal() {
  return `
    <div class="fixed inset-0 bg-black/98 z-[300] flex items-center justify-center p-6 backdrop-blur-3xl">
      <div class="bg-slate-900 border-8 border-orange-600 p-10 rounded-[4rem] text-center max-w-sm">
        <h2 class="text-4xl font-spooky text-orange-500 mb-6 italic">¡DADO DE CASTIGO!</h2>
        ${renderDice(uiState.duenoRoll, uiState.isDuenoRolling)}
        ${(gameState.isHost || !gameState.roomCode) ? `<button onclick="app.rollDueno()" class="w-full mt-8 py-6 bg-orange-600 text-white rounded-3xl font-black text-2xl">LANZAR</button>` : ''}
      </div>
    </div>
  `;
}

function renderBrujasModal() {
  return `
    <div class="fixed inset-0 bg-black/98 z-[300] flex items-center justify-center p-6 backdrop-blur-3xl">
      <div class="bg-slate-900 border-8 border-purple-600 p-10 rounded-[4rem] text-center max-w-sm">
        <h2 class="text-4xl font-spooky text-purple-400 mb-6 italic">¡BRUJAS DE ESCAZÚ!</h2>
        ${renderDice(uiState.brujasRoll, uiState.isBrujasRolling)}
        ${(gameState.isHost || !gameState.roomCode) ? `<button onclick="app.rollBrujas()" class="w-full mt-8 py-6 bg-purple-600 text-white rounded-3xl font-black text-2xl">LANZAR</button>` : ''}
      </div>
    </div>
  `;
}

function renderMenu() {
  return `
    <div class="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-950 text-white font-sans overflow-y-auto">
      <h1 class="text-6xl font-spooky text-orange-500 mb-8 animate-pulse text-center">La Ruta de los Espantos</h1>
      <div class="w-full max-w-md space-y-6 bg-gray-800 p-8 rounded-3xl border border-gray-700 shadow-2xl relative">
        <input id="playerNameInput" type="text" value="${escapeHtml(uiState.playerName)}" class="w-full bg-slate-800 border border-slate-700 p-4 rounded-2xl outline-none" placeholder="Nombre" />
        <div class="flex justify-between items-center bg-slate-800 border border-slate-700 p-4 rounded-2xl">
          <label for="numPlayers" class="font-bold">Jugadores:</label>
          <select id="numPlayers" class="bg-transparent outline-none font-bold text-lg">
            ${[2, 3, 4].map((num) => `<option value="${num}" ${uiState.numLocalPlayers === num ? 'selected' : ''}>${num}</option>`).join('')}
          </select>
        </div>
        <div class="flex justify-between items-center bg-slate-800 border border-slate-700 p-4 rounded-2xl">
          <label for="monaToggleMenu" class="font-bold">Leyenda: La Mona</label>
          <input id="monaToggleMenu" type="checkbox" class="w-5 h-5 accent-orange-500" ${uiState.monaEnabled ? 'checked' : ''} />
        </div>
        <button id="startLocalBtn" class="w-full bg-slate-800 p-4 rounded-2xl font-bold border border-slate-600">Juego Local</button>
        <button id="createRoomBtn" class="w-full bg-orange-600 p-4 rounded-2xl font-bold">Crear Sala</button>
        <div class="flex flex-col sm:flex-row gap-2">
          <input id="roomCodeInput" type="text" value="${escapeHtml(uiState.inputRoomCode)}" class="w-full sm:flex-1 bg-slate-800 p-4 rounded-2xl uppercase" placeholder="CÓDIGO" />
          <button id="joinRoomBtn" class="w-full sm:w-auto bg-indigo-600 px-6 py-4 rounded-2xl font-bold">Unirse</button>
        </div>
        <button id="scanQrBtn" class="w-full bg-slate-800 p-4 rounded-2xl font-bold">Escanear QR</button>
        <a href="/" class="w-full bg-slate-900/70 p-4 rounded-2xl font-bold border border-slate-700 text-center hover:border-orange-400 transition-all">Historia y Leyendas</a>
      </div>
      ${uiState.isScannerOpen ? `
        <div class="fixed inset-0 bg-black/95 z-[500] flex flex-col items-center justify-center p-6 text-white">
          <div class="w-full max-w-md bg-slate-900 p-4 rounded-3xl border border-slate-700">
            <div id="reader"></div>
            <button onclick="app.closeScanner()" class="w-full mt-4 bg-slate-800 p-3 rounded-xl font-bold">Cancelar</button>
          </div>
        </div>
      ` : ''}
    </div>
  `;
}

function renderLobby() {
  const players = gameState.players
    .map(
      (p) => `
        <div class="bg-slate-800/50 p-3 rounded-xl flex items-center gap-3 border border-slate-700">
          <div class="w-3 h-3 rounded-full" style="background-color: ${p.color}"></div>
          <span class="font-bold">${escapeHtml(p.name)} ${p.peerId === myId ? '(Tú)' : ''}</span>
        </div>
      `
    )
    .join('');

  return `
    <div class="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-950 text-white overflow-y-auto">
      <div class="w-full max-lg bg-slate-900 p-8 rounded-[2.5rem] border border-slate-700 text-center relative shadow-2xl">
        <button onclick="app.resetToMenu()" class="absolute top-6 right-6 text-slate-500 font-bold">✖</button>
        <h2 class="text-3xl font-spooky text-orange-500 mb-6">Lobby</h2>
        <div class="bg-slate-900/60 border border-slate-700 p-4 rounded-3xl mb-4 inline-block">
          ${uiState.qrDataUrl
            ? `<img src="${uiState.qrDataUrl}" alt="Room QR" class="w-40 h-40 bg-white p-2 rounded-2xl" />`
            : `<div class="w-40 h-40 flex items-center justify-center text-[10px] uppercase tracking-widest text-slate-400">Generando QR...</div>`}
        </div>
        <div class="flex flex-col items-center gap-2 mb-4">
          <p class="text-xs font-mono text-orange-400 break-all">${escapeHtml(myId)}</p>
          <button onclick="app.copyRoomCode()" class="px-4 py-2 rounded-xl bg-slate-800 border border-slate-700 text-[10px] font-black uppercase tracking-widest text-orange-300 hover:border-orange-400 transition-all">Copiar c&oacute;digo</button>
        </div>
        <p class="text-slate-500 text-[10px] uppercase tracking-widest mb-6">Comparte este c\u00f3digo completo</p>
        <div class="space-y-2 mb-8 text-left">
          ${players}
        </div>
        <div class="bg-slate-800/50 p-3 rounded-xl border border-slate-700 flex items-center justify-between mb-6">
          <span class="text-sm font-bold text-slate-200">Leyenda: La Mona</span>
          ${
            gameState.isHost
              ? `<label class="flex items-center gap-2 text-xs font-bold text-orange-300">
                  <input id="monaToggleLobby" type="checkbox" class="w-4 h-4 accent-orange-500" ${gameState.monaEnabled ? 'checked' : ''} />
                  <span>${gameState.monaEnabled ? 'Incluida' : 'No incluida'}</span>
                </label>`
              : `<span class="text-xs font-bold text-orange-300">${gameState.monaEnabled ? 'Incluida' : 'No incluida'}</span>`
          }
        </div>
        ${gameState.isHost ? '<button onclick="app.initializeMatch()" class="w-full bg-green-600 p-4 rounded-2xl font-bold text-xl">¡EMPEZAR!</button>' : ''}
      </div>
    </div>
  `;
}

function renderPlaying() {
  const myP = getMyPlayer();
  const myTurn = isMyTurn();

  const playersHeader = gameState.players
    .map((p, idx) => {
      const isOut = p.tamales <= 0;
      return `
        <div class="relative w-9 h-9 rounded-full border-2 flex items-center justify-center text-[10px] font-bold ${
          gameState.currentPlayerIndex === idx ? 'border-white scale-110 shadow-[0_0_15px_white]' : 'border-transparent opacity-40'
        } ${isOut ? 'grayscale' : ''}" style="background-color: ${p.color}">
          ${isOut ? '💀' : escapeHtml(p.name[0])}
          ${p.isFinished && !isOut ? '<span class="absolute -top-1 -right-1 text-[10px]">🚩</span>' : ''}
        </div>
      `;
    })
    .join('');

  const tiles = visualIndices
    .map((tileIndex) => {
      const tile = getBoard()[tileIndex];
      const playersHere = gameState.players.filter((p) => p.position === tileIndex && !p.isFinished && p.tamales > 0);
      const legend = tile.legendId ? LEGENDS_DATA[tile.legendId] : null;
      const tokens = playersHere
        .map(
          (p) => `<div class="w-4 h-4 md:w-6 md:h-6 rounded-full border-2 border-white/80 player-token" style="background-color: ${p.color}"></div>`
        )
        .join('');
      return `
        <div ${legend ? `onclick="app.viewLegend('${legend.id}')"` : ''} class="relative rounded-xl border-2 flex flex-col items-center justify-center p-1 bg-slate-800/50 border-slate-700/50 group cursor-pointer transition-all">
          ${renderIcon(legend?.icon || tile.icon || '🌲', 'text-xl md:text-3xl drop-shadow-lg', '💀')}
          <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
            ${tokens}
          </div>
        </div>
      `;
    })
    .join('');

  return `
    <div class="min-h-screen bg-slate-950 p-4 flex flex-col items-center overflow-y-auto">
      <div class="w-full max-w-4xl flex justify-between items-center mb-4 bg-slate-900/80 p-3 rounded-2xl border border-slate-700 shadow-xl backdrop-blur-md sticky top-0 z-[50]">
        <div class="flex items-center gap-3"><h1 class="text-lg font-spooky text-orange-500">La Ruta</h1></div>
        <div class="flex gap-2">
          ${playersHeader}
          <button onclick="app.openHelp()" class="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-orange-500 font-black">?</button>
        </div>
      </div>

      <div class="relative w-full max-w-4xl aspect-[5/6] bg-slate-900 p-4 rounded-[2rem] border-4 border-slate-800 shadow-[inset_0_0_80px_rgba(0,0,0,0.8)] overflow-hidden shrink-0">
        <div class="grid grid-cols-5 grid-rows-6 gap-2 h-full">
          ${tiles}
        </div>
      </div>

      <div class="w-full max-w-4xl grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div class="bg-slate-900/60 p-5 rounded-2xl border border-slate-800 min-h-[100px] shadow-lg">
          <h4 class="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1">El Narrador</h4>
          <p class="text-sm text-indigo-100 italic leading-snug">"${escapeHtml(gameState.narratorComment)}"</p>
        </div>
        <div class="bg-slate-900 p-5 rounded-2xl border border-slate-700 flex flex-col items-center justify-between gap-4 shadow-2xl">
          ${renderDice(gameState.diceResult, gameState.isRolling)}
          <button
            onclick="app.rollDice()"
            ${gameState.isRolling || !myTurn || !!gameState.winner || (myP?.tamales || 0) <= 0 || myP?.isFinished ? 'disabled' : ''}
            class="w-full py-4 rounded-xl font-black text-sm uppercase transition-all ${
              myTurn && !gameState.isRolling && !myP?.isFinished && (myP?.tamales || 0) > 0
                ? 'bg-orange-600 hover:bg-orange-500 shadow-[0_6px_0_0_#9a3412] active:translate-y-1'
                : 'bg-slate-800 text-slate-600'
            }"
          >
            ${gameState.isRolling ? '...' : (myP?.isFinished ? '¡En Meta!' : ((myP?.tamales || 0) <= 0 ? '¡Muerto!' : 'Lanzar'))}
          </button>
        </div>
        <div class="bg-slate-900/60 p-5 rounded-2xl border border-slate-800 flex flex-col gap-4 shadow-lg">
          <div class="flex justify-between items-center bg-yellow-950/20 p-3 rounded-xl border border-yellow-900/30">
            <span class="text-xs font-bold text-yellow-600 uppercase">Tamales</span>
            <span class="text-xl font-black ${(myP?.tamales || 0) <= 0 ? 'text-red-500' : 'text-yellow-500'}">🫔 ${myP?.tamales || 0}</span>
          </div>
          <div class="flex justify-between items-center bg-amber-950/20 p-3 rounded-xl border border-amber-900/30">
            <span class="text-xs font-bold text-amber-500 uppercase">Arroz con Leche</span>
            <span class="text-xl font-black text-amber-400">🍮 ${myP?.arroz || 0}</span>
          </div>
          <button
            onclick="app.openInventory()"
            ${!myP || myP.tamales <= 0 || myP.isFinished ? 'disabled' : ''}
            class="w-full py-2 bg-indigo-900/40 rounded-xl text-indigo-300 text-xs font-bold border border-indigo-500/30 disabled:opacity-30"
          >
            Mochila 🎒
          </button>
        </div>
      </div>

      ${uiState.isDuenoVisible ? renderDuenoModal() : ''}
      ${uiState.isBrujasVisible ? renderBrujasModal() : ''}
      ${uiState.isTurnoVisible ? renderTurnoModal() : ''}
      ${
        gameState.activeChoice
          ? (() => {
              const activeChoice = gameState.activeChoice;
              const choicePlayer = activeChoice.targetId != null
                ? gameState.players.find((p) => p.id === activeChoice.targetId)
                : gameState.players[activeChoice.playerIdx];
              const isChoiceControl = gameState.roomCode
                ? choicePlayer?.peerId === myId
                : true;
              return renderChoiceModal(activeChoice, isChoiceControl, choicePlayer?.tamales || 0);
            })()
          : ''
      }
      ${gameState.activeEvent ? renderEventPopup(gameState.activeEvent, gameState.isHost || myTurn) : ''}
      ${uiState.showTargetSelector && !uiState.casadoTargetId && !uiState.stealTargetId && !uiState.caldosaShareTargetId ? renderTargetSelector() : ''}
      ${uiState.casadoTargetId ? renderCasadoSelector() : ''}
      ${uiState.stealTargetId ? renderStealSelector() : ''}
      ${uiState.caldosaShareTargetId ? renderCaldosaShareSelector() : ''}
      ${uiState.showLegendSelector ? renderLegendSelector() : ''}
      ${uiState.viewingLegend ? renderLegendModal(uiState.viewingLegend) : ''}
      ${gameState.winner ? renderWinnerModal() : ''}
      ${uiState.isInventoryOpen ? renderInventoryModal(myP?.inventory || [], myTurn && !gameState.isRolling) : ''}
      ${uiState.isHelpOpen ? renderHelpModal() : ''}
    </div>
  `;
}

function render() {
  if (!appEl) return;
  let html = '';
  if (gameState.status === 'menu') html = renderMenu();
  else if (gameState.status === 'lobby') html = renderLobby();
  else html = renderPlaying();
  if (uiState.uiAlert) html += renderUiAlert(uiState.uiAlert);
  appEl.innerHTML = html;
  afterRender();
}

function afterRender() {
  if (gameState.status === 'menu') {
    const nameInput = document.getElementById('playerNameInput');
    if (nameInput) {
      nameInput.oninput = (e) => {
        uiState.playerName = e.target.value;
      };
    }
    const roomInput = document.getElementById('roomCodeInput');
    if (roomInput) {
      roomInput.oninput = (e) => {
        uiState.inputRoomCode = e.target.value;
      };
    }
    const numSelect = document.getElementById('numPlayers');
    if (numSelect) {
      numSelect.onchange = (e) => {
        uiState.numLocalPlayers = Number(e.target.value);
      };
    }
    const monaToggleMenu = document.getElementById('monaToggleMenu');
    if (monaToggleMenu) {
      monaToggleMenu.onchange = (e) => setMonaEnabled(e.target.checked);
    }
    const startLocalBtn = document.getElementById('startLocalBtn');
    if (startLocalBtn) startLocalBtn.onclick = () => startLocalGame();
    const createRoomBtn = document.getElementById('createRoomBtn');
    if (createRoomBtn) createRoomBtn.onclick = () => createRoom();
    const joinRoomBtn = document.getElementById('joinRoomBtn');
    if (joinRoomBtn) joinRoomBtn.onclick = () => joinRoom();
    const scanQrBtn = document.getElementById('scanQrBtn');
    if (scanQrBtn) scanQrBtn.onclick = () => setUiState({ isScannerOpen: true });
  }

  const qrRoomCode = gameState.roomCode || myId;
  if (gameState.status === 'lobby' && qrRoomCode && !uiState.qrDataUrl) {
    if (window.QRCode) {
    window.QRCode.toDataURL(qrRoomCode, { margin: 2, scale: 8, color: { dark: '#f97316', light: '#ffffff' } })
      .then((url) => setUiState({ qrDataUrl: url }))
      .catch(() => {});
    } else {
      scheduleQrRetry();
    }
  }
  if (gameState.status === 'lobby' && gameState.isHost) {
    const monaToggleLobby = document.getElementById('monaToggleLobby');
    if (monaToggleLobby) {
      monaToggleLobby.onchange = (e) => setMonaEnabled(e.target.checked);
    }
  }

  if (uiState.isScannerOpen) {
    if (!qrScanner && window.Html5QrcodeScanner) {
      qrScanner = new window.Html5QrcodeScanner('reader', { fps: 10, qrbox: { width: 250, height: 250 } }, false);
      qrScanner.render(
        (decodedText) => {
          qrScanner.clear().then(() => {
            qrScanner = null;
            setUiState({ isScannerOpen: false });
            joinRoom(decodedText);
          });
        },
        () => {}
      );
    }
  } else if (qrScanner) {
    qrScanner.clear().catch(() => {}).finally(() => { qrScanner = null; });
  }

  document.querySelectorAll('img[data-fallback]').forEach((img) => {
    img.onerror = () => {
      const span = document.createElement('span');
      span.className = img.className;
      span.textContent = img.dataset.fallback || '??';
      img.replaceWith(span);
    };
  });

  advanceTurnIfNeeded();
}

function initPeer() {
  if (!window.Peer) return;
  peer = new window.Peer();
  peer.on('open', (id) => {
    myId = id;
    if (gameState.status === 'lobby' && gameState.isHost) {
      const updatedPlayers = gameState.players.map((p, idx) => (
        idx == 0 ? { ...p, peerId: id } : p
      ));
      setGameState((prev) => ({
        ...prev,
        roomCode: id,
        players: updatedPlayers
      }));
      setUiState({ qrDataUrl: '' });
    }
    render();
  });
  peer.on('error', (err) => {
    console.error('Peer error:', err);
    showUiAlert('Servidor de salas', 'Error al conectar. Intenta recargar la pagina.', 'BAD');
  });
  peer.on('connection', (conn) => {
    if (!gameState.isHost) {
      conn.close();
      return;
    }
    conn.on('open', () => {
      registerConnection(conn);
      sendStateToConnection(conn, gameState);
    });
    conn.on('data', (data) => handleIncomingData(data, conn));
  });
}

window.app = {
  setPlayerName: (value) => setUiState({ playerName: value }),
  setInputRoomCode: (value) => setUiState({ inputRoomCode: value }),
  setNumLocalPlayers: (value) => setUiState({ numLocalPlayers: Number(value) }),
  startLocalGame,
  createRoom,
  joinRoom: () => joinRoom(),
  openScanner: () => setUiState({ isScannerOpen: true }),
  closeScanner: () => setUiState({ isScannerOpen: false }),
  resetToMenu,
  initializeMatch,
  rollDice: () => {
    if (gameState.isHost) processDiceRoll();
    else connections[0]?.send({ type: 'ROLL_DICE' });
  },
  openHelp: () => setUiState({ isHelpOpen: true }),
  closeHelp: () => setUiState({ isHelpOpen: false }),
  closeUiAlert: () => setUiState({ uiAlert: null }),
  openInventory: () => setUiState({ isInventoryOpen: true }),
  closeInventory: () => setUiState({ isInventoryOpen: false }),
  useInventoryItem: (item) => {
    if (item === 'Gallo Pinto') {
      setUiState({ isInventoryOpen: false, showLegendSelector: { itemId: item } });
      return;
    }
    if (item === 'Algodón de Azúcar') {
      setUiState({ isInventoryOpen: false });
      const myPlayer = getMyPlayer();
      if (!myPlayer) return;
      if (gameState.isHost) {
        processItemUsage('Algodón de Azúcar', myPlayer.id, myPlayer.id);
      } else {
        connections[0]?.send({ type: 'USE_ITEM', itemId: 'Algodón de Azúcar', targetId: myPlayer.id, fromId: myPlayer.id });
      }
      return;
    }
    if (item === 'Caldosa') {
      const myPlayer = getMyPlayer();
      if (!myPlayer) return;
      const stealCandidates = getCaldosaCandidates([myPlayer.id]);
      if (stealCandidates.length < 2) {
        if (gameState.isHost) {
          const s = {
            ...gameState,
            activeEvent: {
              title: '¡Caldosa!',
              message: 'Necesitas dos jugadores distintos para usarla.',
              icon: '🥣',
              type: 'NEUTRAL'
            }
          };
          setGameState(s);
          broadcastState(s);
        } else {
          showUiAlert('Caldosa', 'Necesitas dos jugadores distintos para usarla.', 'NEUTRAL');
        }
        setUiState({ isInventoryOpen: false, showTargetSelector: null });
        return;
      }
    }
    if (item === 'Suspiros Costarricenses') {
      const myPlayer = getMyPlayer();
      if (!myPlayer) return;
      const deadTargets = gameState.players.filter((p) => p.id !== myPlayer.id && p.tamales <= 0);
      if (!deadTargets.length) {
        if (gameState.isHost) {
          const s = {
            ...gameState,
            activeEvent: {
              title: 'Suspiros Costarricenses',
              message: 'No hay jugadores muertos para revivir.',
              icon: getItemIcon('Suspiros Costarricenses'),
              type: 'NEUTRAL'
            }
          };
          setGameState(s);
          broadcastState(s);
        } else {
          showUiAlert('Suspiros Costarricenses', 'No hay jugadores muertos para revivir.', 'NEUTRAL');
        }
        setUiState({ isInventoryOpen: false, showTargetSelector: null });
        return;
      }
    }
    setUiState({ isInventoryOpen: false, showTargetSelector: { itemId: item } });
  },
  useItemInitiator,
  selectCasadoDestination,
  selectStolenItem,
  selectCaldosaShareTarget,
  selectLegendToRemove,
  makeChoice,
  castMonaVote: (value, voterId) => {
    if (!gameState.monaVote) return;
    if (gameState.isHost) {
      const id = voterId ?? getMyPlayer()?.id;
      if (!id) return;
      registerMonaVote(id, value);
    } else {
      const myPlayer = getMyPlayer();
      if (!myPlayer) return;
      connections[0]?.send({ type: 'MONA_VOTE', voterId: myPlayer.id, value });
    }
  },
  castSanLucasVote: (targetId, voterId) => {
    if (!gameState.sanLucasVote) return;
    if (gameState.isHost) {
      const id = voterId ?? getMyPlayer()?.id;
      if (!id) return;
      registerSanLucasVote(id, targetId);
    } else {
      const myPlayer = getMyPlayer();
      if (!myPlayer) return;
      connections[0]?.send({ type: 'SANLUCAS_VOTE', voterId: myPlayer.id, targetId });
    }
  },
  castRioOroAnswer: (answer, voterId) => {
    if (!gameState.rioOroRiddle) return;
    if (gameState.isHost) {
      const id = voterId ?? getMyPlayer()?.id;
      if (!id) return;
      registerRioOroAnswer(id, answer);
    } else {
      const myPlayer = getMyPlayer();
      if (!myPlayer) return;
      connections[0]?.send({ type: 'RIO_ORO_ANSWER', voterId: myPlayer.id, answer });
    }
  },
  closeEvent: () => {
    if (gameState.isHost) setGameState((s) => ({ ...s, activeEvent: null }));
    else connections[0]?.send({ type: 'CLOSE_EVENT' });
  },
  viewLegend: (id) => setUiState({ viewingLegend: LEGENDS_DATA[id] || null }),
  closeLegend: () => setUiState({ viewingLegend: null }),
  clearTargetSelector: () => setUiState({ showTargetSelector: null }),
  clearCasadoTarget: () => setUiState({ casadoTargetId: null }),
  clearStealTarget: () => setUiState({ stealTargetId: null }),
  clearCaldosaTargets: () => setUiState({ showTargetSelector: null, caldosaStealTargetId: null, caldosaShareTargetId: null }),
  clearLegendSelector: () => setUiState({ showLegendSelector: null }),
  rollTurno: async () => {
    if (uiState.isTurnoRolling) return;
    setUiState({ isTurnoRolling: true });
    await sleep(1500);
    const r = Math.floor(Math.random() * 6) + 1;
    setUiState({ turnoRoll: r, isTurnoRolling: false });
    await sleep(800);
    window._solveTurno?.(r);
  },
  rollDueno: async () => {
    if (uiState.isDuenoRolling) return;
    setUiState({ isDuenoRolling: true });
    await sleep(1500);
    const r = Math.floor(Math.random() * 6) + 1;
    setUiState({ duenoRoll: r, isDuenoRolling: false });
    await sleep(800);
    window._solveDueno?.(r);
  },
  rollBrujas: async () => {
    if (uiState.isBrujasRolling) return;
    setUiState({ isBrujasRolling: true });
    await sleep(1500);
    const r = Math.floor(Math.random() * 6) + 1;
    setUiState({ brujasRoll: r, isBrujasRolling: false });
    await sleep(800);
    window._solveBrujas?.(r);
  },
  copyRoomCode: async () => {
    const code = gameState.roomCode || myId;
    if (!code) return;
    const fallbackCopy = () => {
      const el = document.createElement('textarea');
      el.value = code;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch {
        success = false;
      }
      document.body.removeChild(el);
      return success;
    };

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        showUiAlert('Codigo copiado', 'El codigo de la sala ya esta en tu portapapeles.', 'GOOD');
        return;
      }
    } catch {
    }

    if (fallbackCopy()) {
      showUiAlert('Codigo copiado', 'El codigo de la sala ya esta en tu portapapeles.', 'GOOD');
    } else {
      showUiAlert('No se pudo copiar', 'Copia el codigo manualmente.', 'NEUTRAL');
    }
  },
};

initPeer();
render();

